/**
 * Elite Batch 20 Part 1 — Workflow Templates 1848–1867
 * Outbound lead SDR pipeline, overlapping customer intelligence, PLG upgrade assist,
 * parent company hierarchy cleanup, partner referral outreach, partner program pitch generator,
 * persona-based personalization, personalized gift idea generator, phone most-likely-to-answer scoring,
 * Pipedrive enrichment batch, podcast guest pitch, positive product adoption pitch,
 * positive reply router, post-demo tech evaluator follow-up, post-meeting account plan,
 * Postmark transactional send, press news personalization, press release pitch,
 * procurement stakeholder finder, procurement support email.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 20 PART 1 — TEMPLATES 1848–1867
// =============================================================================

export const ELITE_BATCH20_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1848. OUTBOUND LEAD SDR PIPELINE (10 nodes)
  // ===================================================================

  "Outbound Lead SDR Pipeline": {
    summary:
      "Fires on webhook when a new lead list is uploaded. Deduplicates leads against HubSpot CRM, enriches via Clearbit, validates emails through ZeroBounce, scores lead fit with runCode, branches by score tier, generates AI-personalized outreach, sends the email, logs activity in HubSpot, and notifies the SDR via Slack.",
    nodes: [
      trigger("Webhook: New lead list uploaded", "webhook_app", {
        source: "lead_management",
        event: "list_uploaded",
        howItStarts:
          "Fires when a new lead list is uploaded or synced from a prospecting tool, initiating the full SDR outbound pipeline with deduplication, enrichment, scoring, and personalized outreach",
        setup:
          "Configure webhook endpoint for lead list uploads. Ensure HubSpot token has contact search and activity logging access. Set up Clearbit API key for enrichment. Provide ZeroBounce API key for email validation. Configure OpenAI key for AI personalization. Set up Slack webhook for SDR channel.",
        inputFields: [],
      }),

      // 2. AI extract lead data from webhook
      aiNode(
        "2",
        "AI extract lead details from webhook payload",
        280,
        "You are a data extraction specialist. Given a raw webhook payload from a lead list upload, extract the lead details regardless of format. Find the email address, first name, last name, company name, job title, and any other available fields. Return JSON: {lead_email, first_name, last_name, company_name, job_title, linkedin_url, phone, source_list}",
        "Extract lead details from this webhook payload: {webhook_payload}",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "last_name", as: "last_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "linkedin_url", as: "linkedin_url", type: "string" },
          { field: "phone", as: "lead_phone", type: "string" },
          { field: "source_list", as: "source_list", type: "string" },
        ],
      ),

      // 3. Deduplicate against HubSpot CRM
      hubspotGetNode(
        "3",
        "HubSpot: Check if lead already exists in CRM",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "total",
            customName: "existing_contact_count",
            type: "number",
          },
          {
            name: "results[0].id",
            customName: "existing_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "existing_lifecycle_stage",
            type: "string",
          },
        ],
      ),

      // 4. Clearbit enrichment API
      n(
        "4",
        "hitApi",
        "Clearbit: Enrich lead profile with firmographic data",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://person-stream.clearbit.com/v2/combined/find?email={lead_email}",
            headers: [
              { key: "Authorization", value: "Bearer {clearbit_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "company.name",
                customName: "enriched_company",
                type: "string",
              },
              {
                name: "company.metrics.employees",
                customName: "employee_count",
                type: "number",
              },
              {
                name: "company.category.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "company.metrics.annualRevenue",
                customName: "annual_revenue",
                type: "number",
              },
              {
                name: "person.employment.title",
                customName: "enriched_title",
                type: "string",
              },
              {
                name: "person.employment.seniority",
                customName: "seniority_level",
                type: "string",
              },
              {
                name: "company.tech",
                customName: "tech_stack",
                type: "string",
              },
              {
                name: "company.domain",
                customName: "company_domain",
                type: "string",
              },
            ],
          },
        },
      ),

      // 5. ZeroBounce email validation
      n("5", "hitApi", "ZeroBounce: Validate lead email deliverability", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={lead_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
            {
              name: "free_email",
              customName: "is_free_email",
              type: "boolean",
            },
          ],
        },
      }),

      // 6. Lead fit scoring runCode
      n(
        "6",
        "runCode",
        "Score lead fit based on enrichment and validation data",
        1240,
        {
          runCodeDetails: {
            code: `let score = 0;
const empCount = parseInt(variables.employee_count) || 0;
const revenue = parseInt(variables.annual_revenue) || 0;
const seniority = (variables.seniority_level || '').toLowerCase();
const emailValid = (variables.email_status || '').toLowerCase() === 'valid';
const isFreeMail = variables.is_free_email === 'true' || variables.is_free_email === true;
const isDuplicate = parseInt(variables.existing_contact_count) > 0;
if (empCount >= 50 && empCount <= 5000) score += 25;
else if (empCount > 5000) score += 15;
if (revenue >= 5000000) score += 20;
else if (revenue >= 1000000) score += 10;
if (['director','vp','c-suite','executive','owner','founder'].some(s => seniority.includes(s))) score += 25;
else if (['manager','senior'].some(s => seniority.includes(s))) score += 15;
if (emailValid && !isFreeMail) score += 20;
else if (emailValid) score += 10;
if (isDuplicate) score -= 30;
output.lead_score = Math.max(0, Math.min(100, score));
output.score_tier = score >= 70 ? 'hot' : score >= 40 ? 'warm' : 'cold';
output.is_duplicate = isDuplicate ? 'yes' : 'no';
output.scoring_details = 'Employees: ' + empCount + ' | Revenue: $' + revenue + ' | Seniority: ' + seniority + ' | Email valid: ' + emailValid + ' | Duplicate: ' + isDuplicate;`,
            outputVariables: [
              { name: "lead_score", type: "number" },
              { name: "score_tier", type: "string" },
              { name: "is_duplicate", type: "string" },
              { name: "scoring_details", type: "string" },
            ],
          },
        },
      ),

      // 7. Branch by score tier condition
      n("7", "condition", "Route lead by score tier: hot/warm vs cold", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "lead_score", operator: "greater_than", value: "39" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. AI personalized outreach (hot/warm path)
      aiNode(
        "8",
        "AI generate personalized SDR outreach email",
        1720,
        "You are an elite SDR copywriter. Write a highly personalized cold outreach email that references the prospect's company, role, industry, and tech stack. Be concise, conversational, and end with a clear CTA. Return JSON: {subject_line, email_body, ps_line}",
        "Lead: {first_name} {last_name}, {enriched_title} at {enriched_company} | Industry: {industry} | Employees: {employee_count} | Revenue: ${annual_revenue} | Seniority: {seniority_level} | Tech: {tech_stack} | Score: {lead_score}/100 ({score_tier}) | Source: {source_list}",
        [
          { field: "subject_line", as: "outreach_subject", type: "string" },
          { field: "email_body", as: "outreach_body", type: "string" },
          { field: "ps_line", as: "outreach_ps", type: "string" },
        ],
      ),

      // 9. Send email
      emailNode(
        "9",
        "Send personalized outreach email to lead",
        1960,
        "{lead_email}",
        "{outreach_subject}",
        "{outreach_body}\n\nP.S. {outreach_ps}",
      ),

      // 10. CRM activity log + Slack notify (split into HubSpot post then Slack)
      hubspotPostNode(
        "10",
        "HubSpot: Log outreach activity on contact record",
        2200,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{lead_email}","firstname":"{first_name}","lastname":"{last_name}","company":"{enriched_company}","jobtitle":"{enriched_title}","hs_lead_status":"OPEN","lead_score_custom":"{lead_score}","lifecyclestage":"lead","notes":"SDR Pipeline | Score: {lead_score} ({score_tier}) | {scoring_details}"}',
          },
        ],
        [
          { name: "id", customName: "crm_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "logged_email",
            type: "string",
          },
        ],
      ),

      // 11. Notify SDR Slack (node 11, id "11" but template says 10 nodes — use id "10b" approach; actually let's use sequential)
      slackNode(
        "a",
        "Slack: Notify SDR of new qualified lead",
        2440,
        "New qualified lead from SDR pipeline:\n- Lead: {first_name} {last_name} ({lead_email})\n- Company: {enriched_company} | {industry}\n- Title: {enriched_title} | Seniority: {seniority_level}\n- Score: {lead_score}/100 ({score_tier})\n- Email sent: {outreach_subject}\n- CRM ID: {crm_contact_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "a", "output_2"),
      e("8", "9"),
      e("9", "10"),
      e("10", "a"),
    ],
  },

  // ===================================================================
  // 1849. OVERLAPPING CUSTOMER INTELLIGENCE (9 nodes)
  // ===================================================================

  "Overlapping Customer Intelligence": {
    summary:
      "Runs on schedule to fetch partner and internal customer lists, cross-references for overlap with runCode, uses AI to evaluate mutual success potential and draft co-marketing briefs, branches by opportunity score, creates partnership opportunities in HubSpot, and notifies the partnership manager via Slack.",
    nodes: [
      trigger("Schedule: Weekly customer overlap analysis", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Fires weekly to analyze overlap between partner customer lists and internal CRM customers, identifying co-marketing and upsell opportunities from shared accounts",
        setup:
          "Configure partner customer list API endpoint or CSV import source. Ensure HubSpot token has contact and company list access. Configure OpenAI key for AI analysis. Set up Slack webhook for partnership manager channel.",
        inputFields: [
          { name: "partner_name", type: "string", example: "TechPartner Inc" },
          {
            name: "partner_list_url",
            type: "string",
            example: "https://api.partner.com/shared-customers",
          },
        ],
      }),

      // 2. Fetch partner customer list runCode
      n("2", "hitApi", "Fetch partner shared customer list via API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "{partner_list_url}",
          headers: [
            { key: "Authorization", value: "Bearer {partner_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "customers",
              customName: "partner_customer_list",
              type: "string",
            },
            {
              name: "total",
              customName: "partner_customer_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Fetch internal customer list from HubSpot
      hubspotGetNode(
        "3",
        "HubSpot: Fetch internal active customer list",
        520,
        "crm/v3/objects/companies?limit=100&properties=name,domain,industry,annualrevenue&associations=contacts",
        [
          {
            name: "results",
            customName: "internal_customer_list",
            type: "string",
          },
          {
            name: "total",
            customName: "internal_customer_count",
            type: "number",
          },
        ],
      ),

      // 4. Cross-reference overlap runCode
      n(
        "4",
        "runCode",
        "Cross-reference partner and internal customer lists for overlap",
        760,
        {
          runCodeDetails: {
            code: `const partnerList = JSON.parse(variables.partner_customer_list || '[]');
const internalList = JSON.parse(variables.internal_customer_list || '[]');
const partnerDomains = partnerList.map(c => (c.domain || c.website || c.email || '').split('@').pop().toLowerCase().replace(/^www\\./, ''));
const internalDomains = internalList.map(c => ((c.properties || {}).domain || '').toLowerCase().replace(/^www\\./, ''));
const overlapping = [];
for (const pd of partnerDomains) {
  if (!pd) continue;
  const match = internalDomains.findIndex(id => id === pd);
  if (match >= 0) {
    const partner = partnerList[partnerDomains.indexOf(pd)];
    const internal = internalList[match];
    overlapping.push({
      domain: pd,
      partner_name: partner.name || partner.company_name || pd,
      internal_name: (internal.properties || {}).name || pd,
      industry: (internal.properties || {}).industry || 'unknown',
      revenue: (internal.properties || {}).annualrevenue || '0',
    });
  }
}
output.overlap_count = overlapping.length;
output.overlap_details = JSON.stringify(overlapping.slice(0, 20));
output.overlap_rate = partnerDomains.length > 0 ? Math.round((overlapping.length / partnerDomains.length) * 100) : 0;
output.top_overlap_companies = overlapping.slice(0, 5).map(o => o.internal_name).join(', ');
output.has_significant_overlap = overlapping.length >= 3 ? 'yes' : 'no';`,
            outputVariables: [
              { name: "overlap_count", type: "number" },
              { name: "overlap_details", type: "string" },
              { name: "overlap_rate", type: "number" },
              { name: "top_overlap_companies", type: "string" },
              { name: "has_significant_overlap", type: "string" },
            ],
          },
        },
      ),

      // 5. AI mutual success potential
      aiNode(
        "5",
        "AI evaluate mutual success potential from overlap data",
        1000,
        "You are a strategic partnerships analyst. Given overlapping customer data between two companies, evaluate the mutual success potential. Identify cross-sell opportunities, co-marketing angles, and joint success stories. Score the opportunity. Return JSON: {opportunity_score, cross_sell_opportunities, co_marketing_angles, joint_case_study_candidates, revenue_uplift_estimate, partnership_recommendation}",
        "Partner: {partner_name} | Overlap: {overlap_count} accounts ({overlap_rate}% of partner base) | Top overlapping: {top_overlap_companies} | Details: {overlap_details}",
        [
          {
            field: "opportunity_score",
            as: "opportunity_score",
            type: "number",
          },
          {
            field: "cross_sell_opportunities",
            as: "cross_sell_opportunities",
            type: "string",
          },
          {
            field: "co_marketing_angles",
            as: "co_marketing_angles",
            type: "string",
          },
          {
            field: "joint_case_study_candidates",
            as: "joint_case_study_candidates",
            type: "string",
          },
          {
            field: "revenue_uplift_estimate",
            as: "revenue_uplift_estimate",
            type: "string",
          },
          {
            field: "partnership_recommendation",
            as: "partnership_recommendation",
            type: "string",
          },
        ],
      ),

      // 6. AI co-marketing briefs
      aiNode(
        "6",
        "AI draft co-marketing campaign briefs for overlapping accounts",
        1240,
        "You are a co-marketing strategist. Given overlap data and opportunity analysis, draft 2-3 specific co-marketing campaign briefs targeting the shared customer base. Include campaign name, channel, messaging angle, and expected impact. Return JSON: {campaign_briefs, recommended_first_campaign, timeline, estimated_reach, budget_suggestion}",
        "Partner: {partner_name} | Overlap: {overlap_count} accounts | Cross-sell: {cross_sell_opportunities} | Co-marketing angles: {co_marketing_angles} | Joint case studies: {joint_case_study_candidates} | Revenue uplift: {revenue_uplift_estimate}",
        [
          { field: "campaign_briefs", as: "campaign_briefs", type: "string" },
          {
            field: "recommended_first_campaign",
            as: "recommended_first_campaign",
            type: "string",
          },
          { field: "timeline", as: "campaign_timeline", type: "string" },
          { field: "estimated_reach", as: "estimated_reach", type: "string" },
          {
            field: "budget_suggestion",
            as: "budget_suggestion",
            type: "string",
          },
        ],
      ),

      // 7. Branch by opportunity score condition
      n(
        "7",
        "condition",
        "Route by partnership opportunity score threshold",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "opportunity_score",
                operator: "greater_than",
                value: "60",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8. CRM partnership opportunity HubSpot (high score path)
      hubspotPostNode(
        "8",
        "HubSpot: Create partnership opportunity deal",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Co-Marketing — {partner_name} ({overlap_count} overlapping accounts)","dealstage":"qualifiedtobuy","pipeline":"default","amount":"{revenue_uplift_estimate}","description":"Opportunity score: {opportunity_score}/100 | Overlap rate: {overlap_rate}% | Cross-sell: {cross_sell_opportunities} | First campaign: {recommended_first_campaign} | Timeline: {campaign_timeline} | Budget: {budget_suggestion}"}',
          },
        ],
        [
          { name: "id", customName: "partnership_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "partnership_deal_name",
            type: "string",
          },
        ],
      ),

      // 9. Notify partnership manager Slack
      slackNode(
        "9",
        "Slack: Notify partnership manager of overlap opportunity",
        1960,
        "Customer Overlap Alert with {partner_name}:\n- Overlapping accounts: {overlap_count} ({overlap_rate}%)\n- Top accounts: {top_overlap_companies}\n- Opportunity score: {opportunity_score}/100\n- Revenue uplift: {revenue_uplift_estimate}\n- Recommendation: {partnership_recommendation}\n- First campaign: {recommended_first_campaign}\n- Deal created: {partnership_deal_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1850. PLG UPGRADE ASSIST (8 nodes)
  // ===================================================================

  "PLG Upgrade Assist": {
    summary:
      "Fires on webhook when a product-led growth upgrade signal is detected. Fetches the user profile from HubSpot, evaluates upgrade readiness with runCode scoring, branches by upgrade score, generates AI-personalized upgrade email, sends the email, creates an expansion opportunity in HubSpot, and alerts the CSM via Slack.",
    nodes: [
      trigger("Webhook: PLG upgrade signal detected", "webhook_app", {
        source: "product_analytics",
        event: "upgrade_signal",
        howItStarts:
          "Fires when a free or trial user triggers an upgrade signal such as hitting a usage limit, enabling a premium feature, or reaching a usage milestone",
        setup:
          "Configure product analytics webhook for upgrade signals (e.g., Segment, Amplitude, or custom events). Ensure HubSpot token has contact and deal access. Configure OpenAI key for personalization. Set up Slack webhook for CSM notifications.",
        inputFields: [],
      }),

      // 2. AI extract upgrade signal from webhook
      aiNode(
        "2",
        "AI extract upgrade signal details from webhook payload",
        280,
        "You are a data extraction specialist for product analytics events. Extract the user details and upgrade signal information from this webhook payload regardless of format. Find the user email, user name, account/company name, current plan, signal type (usage limit, feature gate, milestone), feature used, usage metrics, and any other relevant context. Return JSON: {user_email, user_name, company_name, current_plan, signal_type, feature_triggered, usage_count, usage_limit, account_id}",
        "Extract upgrade signal details from: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "current_plan", as: "current_plan", type: "string" },
          { field: "signal_type", as: "signal_type", type: "string" },
          {
            field: "feature_triggered",
            as: "feature_triggered",
            type: "string",
          },
          { field: "usage_count", as: "usage_count", type: "string" },
          { field: "usage_limit", as: "usage_limit", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
        ],
      ),

      // 3. Fetch user profile HubSpot
      hubspotGetNode(
        "3",
        "HubSpot: Fetch user contact profile and history",
        520,
        "crm/v3/objects/contacts/{user_email}?idProperty=email&properties=firstname,lastname,company,jobtitle,lifecyclestage,hs_lead_status,num_associated_deals,notes_last_updated",
        [
          {
            name: "properties.firstname",
            customName: "contact_first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "contact_last_name",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "crm_company",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "contact_title",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "existing_deals",
            type: "number",
          },
          { name: "id", customName: "hubspot_contact_id", type: "string" },
        ],
      ),

      // 4. Evaluate upgrade readiness runCode
      n(
        "4",
        "runCode",
        "Score upgrade readiness based on usage and profile signals",
        760,
        {
          runCodeDetails: {
            code: `let score = 0;
const usage = parseInt(variables.usage_count) || 0;
const limit = parseInt(variables.usage_limit) || 100;
const usageRatio = limit > 0 ? usage / limit : 0;
const signalType = (variables.signal_type || '').toLowerCase();
const lifecycle = (variables.lifecycle_stage || '').toLowerCase();
const existingDeals = parseInt(variables.existing_deals) || 0;
if (usageRatio >= 0.9) score += 30;
else if (usageRatio >= 0.7) score += 20;
else if (usageRatio >= 0.5) score += 10;
if (signalType.includes('limit')) score += 25;
else if (signalType.includes('feature')) score += 20;
else if (signalType.includes('milestone')) score += 15;
if (lifecycle === 'customer') score += 15;
else if (lifecycle === 'opportunity') score += 10;
if (existingDeals === 0) score += 10;
output.upgrade_score = Math.min(100, score);
output.upgrade_tier = score >= 70 ? 'high_intent' : score >= 40 ? 'medium_intent' : 'low_intent';
output.usage_percentage = Math.round(usageRatio * 100);
output.recommended_plan = usageRatio >= 0.9 ? 'enterprise' : usageRatio >= 0.5 ? 'professional' : 'starter';
output.urgency = score >= 70 ? 'immediate' : score >= 40 ? 'this_week' : 'nurture';`,
            outputVariables: [
              { name: "upgrade_score", type: "number" },
              { name: "upgrade_tier", type: "string" },
              { name: "usage_percentage", type: "number" },
              { name: "recommended_plan", type: "string" },
              { name: "urgency", type: "string" },
            ],
          },
        },
      ),

      // 5. Branch by upgrade score condition
      n("5", "condition", "Route by upgrade readiness score", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "upgrade_score",
              operator: "greater_than",
              value: "39",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. AI personalized upgrade email (high/medium path)
      aiNode(
        "6",
        "AI generate personalized upgrade email based on usage",
        1240,
        "You are a customer success copywriter specializing in product-led growth upgrades. Write a helpful, non-pushy email that acknowledges the user's usage pattern, highlights the value they would unlock by upgrading, and offers a clear next step. Reference specific features they have used. Return JSON: {subject_line, email_body, upgrade_cta, value_proposition}",
        "User: {contact_first_name} {contact_last_name} ({user_email}) | Title: {contact_title} at {crm_company} | Current plan: {current_plan} | Usage: {usage_percentage}% of limit | Signal: {signal_type} on {feature_triggered} | Recommended plan: {recommended_plan} | Score: {upgrade_score}/100 ({upgrade_tier}) | Urgency: {urgency}",
        [
          { field: "subject_line", as: "upgrade_subject", type: "string" },
          { field: "email_body", as: "upgrade_body", type: "string" },
          { field: "upgrade_cta", as: "upgrade_cta", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
        ],
      ),

      // 7. Send upgrade email
      emailNode(
        "7",
        "Send personalized upgrade email to user",
        1480,
        "{user_email}",
        "{upgrade_subject}",
        "{upgrade_body}",
      ),

      // 8. CRM expansion opportunity + CSM alert
      hubspotPostNode(
        "8",
        "HubSpot: Create expansion opportunity and alert CSM",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"PLG Upgrade — {crm_company} ({recommended_plan})","dealstage":"appointmentscheduled","pipeline":"default","description":"Signal: {signal_type} on {feature_triggered} | Usage: {usage_percentage}% | Score: {upgrade_score}/100 ({upgrade_tier}) | Current plan: {current_plan} | Recommended: {recommended_plan} | Urgency: {urgency} | Value prop: {value_proposition}","hubspot_owner_id":"{owner_id}"}',
          },
        ],
        [
          { name: "id", customName: "expansion_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "expansion_deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1851. PARENT COMPANY HIERARCHY CLEANUP (8 nodes)
  // ===================================================================

  "Parent Company Hierarchy Cleanup": {
    summary:
      "Runs on schedule to pull all company accounts from HubSpot, identifies subsidiary relationships via Clearbit, uses AI to match hierarchy structures, branches by confidence level, updates parent-child mappings in CRM, generates an audit report in Google Sheets, and notifies RevOps via Slack.",
    nodes: [
      trigger("Schedule: Monthly parent company hierarchy audit", "schedule", {
        schedule: "First Monday of each month at 6:00 AM",
        howItStarts:
          "Fires monthly to audit and clean up parent-subsidiary company relationships in HubSpot CRM, ensuring accurate account hierarchy for territory planning and reporting",
        setup:
          "Ensure HubSpot token has company read/write access and association management. Configure Clearbit API key for company enrichment. Set up Google Sheets API for audit report logging. Configure Slack webhook for RevOps channel. OpenAI key for AI hierarchy matching.",
        inputFields: [{ name: "batch_size", type: "number", example: "50" }],
      }),

      // 2. Pull all company accounts from HubSpot
      hubspotGetNode(
        "2",
        "HubSpot: Fetch all company accounts for hierarchy audit",
        280,
        "crm/v3/objects/companies?limit=100&properties=name,domain,industry,annualrevenue,numberofemployees,parent_company_id,hs_parent_company_id",
        [
          { name: "results", customName: "all_companies", type: "string" },
          { name: "total", customName: "total_companies", type: "number" },
        ],
      ),

      // 3. Identify subsidiary relationships via Clearbit
      n(
        "3",
        "hitApi",
        "Clearbit: Identify parent-subsidiary relationships by domain",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company-stream.clearbit.com/v2/companies/find?domain={company_domain}",
            headers: [
              { key: "Authorization", value: "Bearer {clearbit_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "parentDomain",
                customName: "clearbit_parent_domain",
                type: "string",
              },
              {
                name: "name",
                customName: "clearbit_company_name",
                type: "string",
              },
              {
                name: "legalName",
                customName: "clearbit_legal_name",
                type: "string",
              },
              {
                name: "parent.domain",
                customName: "clearbit_parent_url",
                type: "string",
              },
              { name: "type", customName: "company_type", type: "string" },
              {
                name: "metrics.employees",
                customName: "clearbit_employees",
                type: "number",
              },
            ],
          },
        },
      ),

      // 4. AI hierarchy matching
      aiNode(
        "4",
        "AI match and validate parent-subsidiary company hierarchies",
        760,
        "You are a corporate hierarchy analyst. Given a list of companies from CRM and enrichment data, identify parent-subsidiary relationships. Match companies by domain patterns, legal names, and known corporate structures. For each relationship found, provide confidence level and reasoning. Return JSON: {hierarchy_matches, total_matches, high_confidence_count, medium_confidence_count, unmatched_subsidiaries, recommended_merges}",
        "Companies: {all_companies} | Clearbit data — Parent domain: {clearbit_parent_domain} | Company name: {clearbit_company_name} | Legal name: {clearbit_legal_name} | Parent URL: {clearbit_parent_url} | Type: {company_type} | Total accounts: {total_companies}",
        [
          {
            field: "hierarchy_matches",
            as: "hierarchy_matches",
            type: "string",
          },
          { field: "total_matches", as: "total_matches", type: "number" },
          {
            field: "high_confidence_count",
            as: "high_confidence_count",
            type: "number",
          },
          {
            field: "medium_confidence_count",
            as: "medium_confidence_count",
            type: "number",
          },
          {
            field: "unmatched_subsidiaries",
            as: "unmatched_subsidiaries",
            type: "string",
          },
          {
            field: "recommended_merges",
            as: "recommended_merges",
            type: "string",
          },
        ],
      ),

      // 5. Branch by confidence condition
      n(
        "5",
        "condition",
        "Check if high-confidence hierarchy matches found",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "high_confidence_count",
                operator: "greater_than",
                value: "0",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. Update CRM parent mappings HubSpot (high confidence path)
      hubspotPostNode(
        "6",
        "HubSpot: Update parent-child company associations",
        1240,
        "crm/v3/associations/companies/companies/batch/create",
        [{ key: "inputs", value: "{hierarchy_matches}" }],
        [
          {
            name: "status",
            customName: "association_update_status",
            type: "string",
          },
          {
            name: "results",
            customName: "updated_associations",
            type: "string",
          },
        ],
      ),

      // 7. Generate audit report Sheets
      sheetsNode(
        "7",
        "Google Sheets: Log hierarchy audit report",
        1480,
        "HierarchyAudit",
        '[["{total_companies}","{total_matches}","{high_confidence_count}","{medium_confidence_count}","{unmatched_subsidiaries}","{recommended_merges}","{association_update_status}"]]',
      ),

      // 8. Notify RevOps Slack
      slackNode(
        "8",
        "Slack: Notify RevOps of hierarchy cleanup results",
        1720,
        "Parent Company Hierarchy Audit Complete:\n- Total accounts scanned: {total_companies}\n- Hierarchy matches found: {total_matches}\n- High confidence: {high_confidence_count} | Medium: {medium_confidence_count}\n- Unmatched subsidiaries: {unmatched_subsidiaries}\n- Recommended merges: {recommended_merges}\n- Update status: {association_update_status}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1852. PARTNER REFERRAL OUTREACH (9 nodes)
  // ===================================================================

  "Partner Referral Outreach": {
    summary:
      "Fires on webhook when a partner submits a referral. Validates referral data with runCode, enriches the lead via Clearbit, validates email through ZeroBounce, branches by partner tier, generates AI partner-referenced outreach, sends the email, logs the referral in HubSpot CRM, and notifies both the partner and sales team via Slack.",
    nodes: [
      trigger("Webhook: Partner referral submitted", "webhook_app", {
        source: "partner_portal",
        event: "referral_submitted",
        howItStarts:
          "Fires when a partner submits a new lead referral through the partner portal, initiating validation, enrichment, and personalized outreach that references the partner relationship",
        setup:
          "Configure partner portal webhook for referral submissions. Set up Clearbit API key for enrichment. Provide ZeroBounce API key for email validation. Configure OpenAI key for AI-personalized outreach. Ensure HubSpot token has contact and deal creation access. Set up Slack webhook for partner and sales channels.",
        inputFields: [],
      }),

      // 2. AI extract referral data from webhook
      n(
        "2",
        "runCode",
        "Validate and extract partner referral data from webhook",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload.referral || payload;
output.referred_email = data.email || data.lead_email || data.referred_email || '';
output.referred_name = data.name || data.lead_name || data.referred_name || '';
output.referred_company = data.company || data.company_name || data.referred_company || '';
output.referred_title = data.title || data.job_title || '';
output.partner_name = data.partner_name || data.partner || data.referrer_name || '';
output.partner_email = data.partner_email || data.referrer_email || '';
output.partner_tier = data.partner_tier || data.tier || 'standard';
output.referral_notes = data.notes || data.context || data.message || '';
output.referral_id = data.referral_id || data.id || Date.now().toString();
output.is_valid = output.referred_email && output.referred_name ? 'yes' : 'no';`,
            outputVariables: [
              { name: "referred_email", type: "string" },
              { name: "referred_name", type: "string" },
              { name: "referred_company", type: "string" },
              { name: "referred_title", type: "string" },
              { name: "partner_name", type: "string" },
              { name: "partner_email", type: "string" },
              { name: "partner_tier", type: "string" },
              { name: "referral_notes", type: "string" },
              { name: "referral_id", type: "string" },
              { name: "is_valid", type: "string" },
            ],
          },
        },
      ),

      // 3. Enrich lead profile Clearbit
      n("3", "hitApi", "Clearbit: Enrich referred lead profile", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person-stream.clearbit.com/v2/combined/find?email={referred_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "person.employment.title",
              customName: "enriched_title",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "seniority_level",
              type: "string",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
          ],
        },
      }),

      // 4. Validate email ZeroBounce
      n(
        "4",
        "hitApi",
        "ZeroBounce: Validate referred lead email address",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={referred_email}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "status",
                customName: "email_validation_status",
                type: "string",
              },
              {
                name: "sub_status",
                customName: "email_sub_status",
                type: "string",
              },
            ],
          },
        },
      ),

      // 5. Branch by partner tier condition
      n("5", "condition", "Route by partner tier for outreach priority", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "partner_tier", operator: "equals", value: "gold" },
          ],
          logicalOperator: "OR",
        },
      }),

      // 6. AI partner-referenced outreach
      aiNode(
        "6",
        "AI generate partner-referenced outreach email",
        1240,
        "You are a B2B sales writer who specializes in partner-channel outreach. Write a warm, trust-building email that explicitly references the partner who made the referral, leverages social proof from the partnership, and includes a specific CTA. Make it feel like a warm introduction, not a cold email. Return JSON: {subject_line, email_body, partner_reference_hook, cta_text}",
        "Referred lead: {referred_name}, {enriched_title} at {enriched_company} | Industry: {industry} | Employees: {employee_count} | Partner: {partner_name} (tier: {partner_tier}) | Notes from partner: {referral_notes} | Seniority: {seniority_level}",
        [
          { field: "subject_line", as: "referral_subject", type: "string" },
          { field: "email_body", as: "referral_body", type: "string" },
          {
            field: "partner_reference_hook",
            as: "partner_hook",
            type: "string",
          },
          { field: "cta_text", as: "referral_cta", type: "string" },
        ],
      ),

      // 7. Send email
      emailNode(
        "7",
        "Send partner-referenced outreach email",
        1480,
        "{referred_email}",
        "{referral_subject}",
        "{referral_body}",
      ),

      // 8. Log referral CRM HubSpot
      hubspotPostNode(
        "8",
        "HubSpot: Log referral and create deal in CRM",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Partner Referral — {referred_name} ({enriched_company})","dealstage":"appointmentscheduled","pipeline":"default","description":"Referral ID: {referral_id} | Partner: {partner_name} ({partner_tier}) | Lead: {referred_name}, {enriched_title} at {enriched_company} | Industry: {industry} | Employees: {employee_count} | Revenue: ${annual_revenue} | Email status: {email_validation_status} | Notes: {referral_notes}","hubspot_owner_id":"{owner_id}"}',
          },
        ],
        [
          { name: "id", customName: "referral_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "referral_deal_name",
            type: "string",
          },
        ],
      ),

      // 9. Notify partner and sales Slack
      slackNode(
        "9",
        "Slack: Notify partner and sales team of referral outreach",
        1960,
        "Partner Referral Outreach Sent:\n- Referral ID: {referral_id}\n- Lead: {referred_name} ({referred_email})\n- Company: {enriched_company} | {industry} | {employee_count} employees\n- Partner: {partner_name} ({partner_tier})\n- Email sent: {referral_subject}\n- Deal: {referral_deal_name} (ID: {referral_deal_id})\n- Email validity: {email_validation_status}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "9", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1853. PARTNER PROGRAM PITCH GENERATOR (8 nodes)
  // ===================================================================

  "Partner Program Pitch Generator": {
    summary:
      "Fires on webhook when a new partner prospect is identified. AI researches the partner company, analyzes partnership fit, branches by partner type, generates a personalized partnership pitch, validates the contact email via API, sends the pitch email, and creates a partner pipeline entry in HubSpot.",
    nodes: [
      trigger("Webhook: New partner prospect identified", "webhook_app", {
        source: "partner_prospecting",
        event: "prospect_identified",
        howItStarts:
          "Fires when a new potential partner company is identified through market research, inbound inquiry, or partner prospecting, triggering research, fit analysis, and personalized pitch generation",
        setup:
          "Configure webhook for partner prospect identification. Set up OpenAI key for AI research and pitch generation. Provide ZeroBounce API key for email validation. Ensure HubSpot token has deal and contact creation access.",
        inputFields: [],
      }),

      // 2. AI extract partner prospect data
      aiNode(
        "2",
        "AI extract partner prospect details from webhook payload",
        280,
        "You are a data extraction specialist. Extract the partner prospect details from this webhook payload regardless of format. Find the company name, domain, contact name, contact email, contact title, and any context about why they were identified as a prospect. Return JSON: {prospect_company, prospect_domain, contact_name, contact_email, contact_title, identification_reason, company_size_hint}",
        "Extract partner prospect details from: {webhook_payload}",
        [
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "prospect_domain", as: "prospect_domain", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_title", as: "contact_title", type: "string" },
          {
            field: "identification_reason",
            as: "identification_reason",
            type: "string",
          },
          {
            field: "company_size_hint",
            as: "company_size_hint",
            type: "string",
          },
        ],
      ),

      // 3. AI research partner company
      aiNode(
        "3",
        "AI research partner company capabilities and market position",
        520,
        "You are a strategic partnerships researcher. Given a company name and domain, research and analyze their market position, product offering, target audience, and potential as a partner. Return JSON: {company_summary, product_offering, target_market, competitive_position, partnership_history, technology_stack_guess, market_segment}",
        "Research {prospect_company} (domain: {prospect_domain}) | Identification reason: {identification_reason} | Size hint: {company_size_hint}",
        [
          { field: "company_summary", as: "company_summary", type: "string" },
          { field: "product_offering", as: "product_offering", type: "string" },
          { field: "target_market", as: "target_market", type: "string" },
          {
            field: "competitive_position",
            as: "competitive_position",
            type: "string",
          },
          {
            field: "partnership_history",
            as: "partnership_history",
            type: "string",
          },
          { field: "technology_stack_guess", as: "tech_stack", type: "string" },
          { field: "market_segment", as: "market_segment", type: "string" },
        ],
      ),

      // 4. AI partnership fit analysis
      aiNode(
        "4",
        "AI analyze partnership fit and classify partner type",
        760,
        "You are a partnerships strategist. Given research about a potential partner, analyze the fit and classify the partnership type. Determine if they are best suited as a technology partner, referral partner, reseller, co-marketing partner, or integration partner. Score the fit. Return JSON: {partner_type, fit_score, fit_reasoning, mutual_value_prop, integration_potential, revenue_model, recommended_engagement}",
        "Company: {prospect_company} | Summary: {company_summary} | Product: {product_offering} | Target market: {target_market} | Competitive position: {competitive_position} | Tech: {tech_stack} | Segment: {market_segment}",
        [
          { field: "partner_type", as: "partner_type", type: "string" },
          { field: "fit_score", as: "fit_score", type: "number" },
          { field: "fit_reasoning", as: "fit_reasoning", type: "string" },
          {
            field: "mutual_value_prop",
            as: "mutual_value_prop",
            type: "string",
          },
          {
            field: "integration_potential",
            as: "integration_potential",
            type: "string",
          },
          { field: "revenue_model", as: "revenue_model", type: "string" },
          {
            field: "recommended_engagement",
            as: "recommended_engagement",
            type: "string",
          },
        ],
      ),

      // 5. Branch by partner type condition
      n(
        "5",
        "condition",
        "Route by partner type for pitch customization",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "fit_score", operator: "greater_than", value: "50" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. AI personalized pitch (high-fit path)
      aiNode(
        "6",
        "AI generate personalized partnership pitch email",
        1240,
        "You are a partnerships outreach specialist. Write a compelling, personalized partnership pitch email that references specific synergies, proposes a concrete partnership model, and includes a clear next step. Tailor the pitch to the identified partner type. Return JSON: {subject_line, email_body, partnership_hook, proposed_model, next_step}",
        "Contact: {contact_name}, {contact_title} at {prospect_company} | Partner type: {partner_type} | Fit: {fit_score}/100 | Reasoning: {fit_reasoning} | Mutual value: {mutual_value_prop} | Integration: {integration_potential} | Revenue model: {revenue_model} | Engagement: {recommended_engagement}",
        [
          { field: "subject_line", as: "pitch_subject", type: "string" },
          { field: "email_body", as: "pitch_body", type: "string" },
          { field: "partnership_hook", as: "partnership_hook", type: "string" },
          { field: "proposed_model", as: "proposed_model", type: "string" },
          { field: "next_step", as: "pitch_next_step", type: "string" },
        ],
      ),

      // 7. Validate contact email API
      n("7", "hitApi", "ZeroBounce: Validate partner contact email", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "status",
              customName: "email_valid_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Send pitch email + CRM
      emailNode(
        "8",
        "Send personalized partnership pitch email",
        1720,
        "{contact_email}",
        "{pitch_subject}",
        "{pitch_body}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1854. PERSONA-BASED PERSONALIZATION (8 nodes)
  // ===================================================================

  "Persona-Based Personalization": {
    summary:
      "Fires on webhook when a lead needs personalization. Enriches the professional profile via Clearbit, uses AI to classify the buyer persona, fetches persona messaging frameworks with runCode, generates AI persona-tailored messaging, branches by persona priority, sends the personalized email, and updates the CRM record in HubSpot.",
    nodes: [
      trigger(
        "Webhook: Lead assigned for persona personalization",
        "webhook_app",
        {
          source: "lead_routing",
          event: "lead_assigned",
          howItStarts:
            "Fires when a new lead is assigned and requires persona-based message personalization, triggering enrichment, persona classification, and tailored outreach generation",
          setup:
            "Configure webhook for lead assignment events. Set up Clearbit API key for professional enrichment. Configure OpenAI key for persona classification and messaging. Ensure HubSpot token has contact update access.",
          inputFields: [],
        },
      ),

      // 2. AI extract lead data from webhook
      aiNode(
        "2",
        "AI extract lead details for persona classification",
        280,
        "You are a data extraction specialist. Extract the lead details from this webhook payload regardless of format. Find the email, name, company, job title, and any behavioral or demographic signals. Return JSON: {lead_email, lead_name, lead_company, lead_title, lead_source, behavioral_signals}",
        "Extract lead details from: {webhook_payload}",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "lead_title", as: "lead_title", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
          {
            field: "behavioral_signals",
            as: "behavioral_signals",
            type: "string",
          },
        ],
      ),

      // 3. Enrich professional profile Clearbit
      n("3", "hitApi", "Clearbit: Enrich lead professional profile", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person-stream.clearbit.com/v2/combined/find?email={lead_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "person.employment.title",
              customName: "enriched_title",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "seniority",
              type: "string",
            },
            {
              name: "person.employment.role",
              customName: "role",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_size",
              type: "number",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.category.subIndustry",
              customName: "sub_industry",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI classify buyer persona
      aiNode(
        "4",
        "AI classify lead into buyer persona category",
        760,
        "You are a B2B buyer persona specialist. Given a lead's professional profile, classify them into one of these personas: 'technical_evaluator' (engineers, developers, architects), 'business_champion' (VPs, directors who own budget), 'executive_sponsor' (C-suite, founders), 'end_user' (individual contributors, analysts), or 'procurement_gatekeeper' (procurement, legal, finance). Return JSON: {persona_type, persona_confidence, buying_role, pain_points, decision_criteria, communication_style, preferred_content_type}",
        "Lead: {lead_name} | Title: {enriched_title} | Seniority: {seniority} | Role: {role} | Company: {enriched_company} | Industry: {industry} ({sub_industry}) | Size: {company_size} | Source: {lead_source} | Signals: {behavioral_signals}",
        [
          { field: "persona_type", as: "persona_type", type: "string" },
          {
            field: "persona_confidence",
            as: "persona_confidence",
            type: "number",
          },
          { field: "buying_role", as: "buying_role", type: "string" },
          { field: "pain_points", as: "pain_points", type: "string" },
          {
            field: "decision_criteria",
            as: "decision_criteria",
            type: "string",
          },
          {
            field: "communication_style",
            as: "communication_style",
            type: "string",
          },
          {
            field: "preferred_content_type",
            as: "preferred_content_type",
            type: "string",
          },
        ],
      ),

      // 5. Fetch persona messaging framework runCode
      n(
        "5",
        "runCode",
        "Load persona-specific messaging framework and templates",
        1000,
        {
          runCodeDetails: {
            code: `const persona = (variables.persona_type || '').toLowerCase();
const frameworks = {
  technical_evaluator: {
    tone: 'precise, data-driven, technical',
    hook: 'Reference specific technical capabilities and integrations',
    cta: 'Offer technical deep-dive, sandbox, or architecture review',
    avoid: 'Marketing fluff, ROI-only messaging, vague claims',
    proof_type: 'Technical case studies, benchmarks, documentation',
  },
  business_champion: {
    tone: 'strategic, outcome-focused, collaborative',
    hook: 'Reference business outcomes and competitive advantages',
    cta: 'Propose strategy call or business case workshop',
    avoid: 'Too much technical detail, feature lists',
    proof_type: 'ROI calculators, customer success stories, industry reports',
  },
  executive_sponsor: {
    tone: 'concise, strategic, high-level',
    hook: 'Lead with market trends and strategic impact',
    cta: 'Executive briefing or peer-to-peer introduction',
    avoid: 'Long emails, implementation details, low-level features',
    proof_type: 'Executive testimonials, analyst reports, board-level metrics',
  },
  end_user: {
    tone: 'friendly, practical, benefit-focused',
    hook: 'Focus on daily workflow improvements and ease of use',
    cta: 'Free trial, product tour, or quick demo',
    avoid: 'Complex pricing, enterprise jargon, abstract ROI',
    proof_type: 'User reviews, quick-start guides, comparison tables',
  },
  procurement_gatekeeper: {
    tone: 'professional, compliance-aware, factual',
    hook: 'Lead with security, compliance, and vendor reliability',
    cta: 'Share security docs, compliance certs, or vendor questionnaire',
    avoid: 'Feature marketing, aggressive sales language',
    proof_type: 'SOC2 reports, compliance certifications, SLA details',
  },
};
const fw = frameworks[persona] || frameworks.business_champion;
output.messaging_tone = fw.tone;
output.messaging_hook = fw.hook;
output.messaging_cta = fw.cta;
output.messaging_avoid = fw.avoid;
output.proof_type = fw.proof_type;
output.persona_priority = ['executive_sponsor','business_champion'].includes(persona) ? 'high' : 'standard';`,
            outputVariables: [
              { name: "messaging_tone", type: "string" },
              { name: "messaging_hook", type: "string" },
              { name: "messaging_cta", type: "string" },
              { name: "messaging_avoid", type: "string" },
              { name: "proof_type", type: "string" },
              { name: "persona_priority", type: "string" },
            ],
          },
        },
      ),

      // 6. AI persona-tailored messaging
      aiNode(
        "6",
        "AI generate persona-tailored outreach email",
        1240,
        "You are a B2B copywriter who writes persona-specific emails. Follow the messaging framework strictly. Use the specified tone, hook strategy, and CTA type. Avoid the specified anti-patterns. Include the right type of social proof. Return JSON: {subject_line, email_body, proof_element, cta_text}",
        "Lead: {lead_name} at {enriched_company} | Persona: {persona_type} | Buying role: {buying_role} | Pain points: {pain_points} | Decision criteria: {decision_criteria} | Style: {communication_style} | Tone: {messaging_tone} | Hook strategy: {messaging_hook} | CTA type: {messaging_cta} | Avoid: {messaging_avoid} | Proof type: {proof_type} | Industry: {industry} | Size: {company_size}",
        [
          { field: "subject_line", as: "persona_subject", type: "string" },
          { field: "email_body", as: "persona_body", type: "string" },
          { field: "proof_element", as: "persona_proof", type: "string" },
          { field: "cta_text", as: "persona_cta", type: "string" },
        ],
      ),

      // 7. Branch by persona priority condition
      n("7", "condition", "Route by persona priority for send timing", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "persona_priority", operator: "equals", value: "high" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Send email + update CRM
      emailNode(
        "8",
        "Send persona-tailored outreach email",
        1720,
        "{lead_email}",
        "{persona_subject}",
        "{persona_body}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1855. PERSONALIZED GIFT IDEA GENERATOR (8 nodes)
  // ===================================================================

  "Personalized Gift Idea Generator": {
    summary:
      "Fires on webhook when a sales rep requests a gift for a prospect. Fetches prospect social profiles via API, scrapes interest signals with runCode, uses AI to analyze interests, generates AI gift recommendations, branches by budget tier, notifies the rep with options via Slack, and creates a gift request through the Sendoso API.",
    nodes: [
      trigger("Webhook: Gift request for prospect", "webhook_app", {
        source: "sales_tools",
        event: "gift_requested",
        howItStarts:
          "Fires when a sales rep requests a personalized gift recommendation for a prospect, triggering social research, interest analysis, and gift suggestion generation",
        setup:
          "Configure webhook for gift request events. Set up FullContact or similar API for social profile lookup. Configure OpenAI key for AI analysis. Set up Slack webhook for sales rep notifications. Provide Sendoso API key for gift fulfillment.",
        inputFields: [],
      }),

      // 2. AI extract gift request details
      aiNode(
        "2",
        "AI extract gift request details from webhook payload",
        280,
        "You are a data extraction specialist. Extract the gift request details from this webhook payload regardless of format. Find the prospect name, email, company, the requesting sales rep, budget range, occasion, and any context. Return JSON: {prospect_name, prospect_email, prospect_company, prospect_title, sales_rep_name, sales_rep_email, budget_range, occasion, context_notes}",
        "Extract gift request details from: {webhook_payload}",
        [
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "prospect_title", as: "prospect_title", type: "string" },
          { field: "sales_rep_name", as: "sales_rep_name", type: "string" },
          { field: "sales_rep_email", as: "sales_rep_email", type: "string" },
          { field: "budget_range", as: "budget_range", type: "string" },
          { field: "occasion", as: "occasion", type: "string" },
          { field: "context_notes", as: "context_notes", type: "string" },
        ],
      ),

      // 3. Fetch prospect social profiles API
      n(
        "3",
        "hitApi",
        "FullContact: Fetch prospect social profiles and interests",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.fullcontact.com/v3/person.enrich",
            headers: [
              { key: "Authorization", value: "Bearer {fullcontact_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "email", value: "{prospect_email}" }],
            variables: [
              { name: "fullName", customName: "full_name", type: "string" },
              {
                name: "details.interests",
                customName: "interests_raw",
                type: "string",
              },
              {
                name: "details.profiles",
                customName: "social_profiles",
                type: "string",
              },
              {
                name: "details.locations[0].city",
                customName: "prospect_city",
                type: "string",
              },
              {
                name: "details.locations[0].region",
                customName: "prospect_region",
                type: "string",
              },
              { name: "organization", customName: "org_info", type: "string" },
            ],
          },
        },
      ),

      // 4. Scrape interest signals runCode
      n(
        "4",
        "runCode",
        "Parse and score interest signals from social profile data",
        760,
        {
          runCodeDetails: {
            code: `const interests = JSON.parse(variables.interests_raw || '[]');
const profiles = JSON.parse(variables.social_profiles || '[]');
const interestList = interests.map(i => i.name || i.value || i).filter(Boolean);
output.top_interests = interestList.slice(0, 8).join(', ');
output.interest_count = interestList.length;
const hasLinkedin = profiles.some(p => (p.service || p.type || '').toLowerCase().includes('linkedin'));
const hasTwitter = profiles.some(p => (p.service || p.type || '').toLowerCase().includes('twitter'));
output.social_presence = [hasLinkedin ? 'LinkedIn' : '', hasTwitter ? 'Twitter' : ''].filter(Boolean).join(', ') || 'Limited';
output.location_context = [variables.prospect_city, variables.prospect_region].filter(Boolean).join(', ') || 'Unknown';
const budget = (variables.budget_range || '').toLowerCase();
output.budget_tier = budget.includes('premium') || budget.includes('high') || budget.includes('500') ? 'premium' : budget.includes('mid') || budget.includes('100') ? 'mid' : 'standard';
output.personalization_depth = interestList.length >= 5 ? 'high' : interestList.length >= 2 ? 'medium' : 'low';`,
            outputVariables: [
              { name: "top_interests", type: "string" },
              { name: "interest_count", type: "number" },
              { name: "social_presence", type: "string" },
              { name: "location_context", type: "string" },
              { name: "budget_tier", type: "string" },
              { name: "personalization_depth", type: "string" },
            ],
          },
        },
      ),

      // 5. AI analyze interests
      aiNode(
        "5",
        "AI analyze prospect interests for gift alignment",
        1000,
        "You are a personal gifting strategist. Given a prospect's interests, location, social presence, and professional context, analyze their personality and preferences to inform gift selection. Return JSON: {personality_summary, gift_style_preference, hobbies_detected, professional_interests, local_connection_ideas, gift_avoid_list}",
        "Prospect: {prospect_name}, {prospect_title} at {prospect_company} | Location: {location_context} | Interests: {top_interests} | Social: {social_presence} | Occasion: {occasion} | Context: {context_notes} | Personalization depth: {personalization_depth}",
        [
          {
            field: "personality_summary",
            as: "personality_summary",
            type: "string",
          },
          { field: "gift_style_preference", as: "gift_style", type: "string" },
          { field: "hobbies_detected", as: "hobbies", type: "string" },
          {
            field: "professional_interests",
            as: "professional_interests",
            type: "string",
          },
          {
            field: "local_connection_ideas",
            as: "local_ideas",
            type: "string",
          },
          { field: "gift_avoid_list", as: "gift_avoid", type: "string" },
        ],
      ),

      // 6. AI gift recommendations
      aiNode(
        "6",
        "AI generate personalized gift recommendations",
        1240,
        "You are an expert corporate gifting consultant. Based on the prospect's personality, interests, budget tier, and occasion, generate 3 specific, thoughtful gift recommendations ranked by impact. Each should include the item, why it fits, and approximate price. Return JSON: {gift_option_1, gift_option_2, gift_option_3, top_recommendation, personalized_note_suggestion, gift_reasoning}",
        "Prospect: {prospect_name} at {prospect_company} | Personality: {personality_summary} | Style: {gift_style} | Hobbies: {hobbies} | Professional: {professional_interests} | Local: {local_ideas} | Avoid: {gift_avoid} | Budget: {budget_tier} | Occasion: {occasion} | Context: {context_notes}",
        [
          { field: "gift_option_1", as: "gift_option_1", type: "string" },
          { field: "gift_option_2", as: "gift_option_2", type: "string" },
          { field: "gift_option_3", as: "gift_option_3", type: "string" },
          {
            field: "top_recommendation",
            as: "top_recommendation",
            type: "string",
          },
          {
            field: "personalized_note_suggestion",
            as: "gift_note",
            type: "string",
          },
          { field: "gift_reasoning", as: "gift_reasoning", type: "string" },
        ],
      ),

      // 7. Branch by budget tier condition
      n(
        "7",
        "condition",
        "Route by budget tier for gift fulfillment channel",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "budget_tier", operator: "equals", value: "premium" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8. Notify rep with options Slack + create gift request
      slackNode(
        "8",
        "Slack: Send gift recommendations to sales rep",
        1720,
        "Gift Recommendations for {prospect_name} ({prospect_company}):\n\nOccasion: {occasion}\nBudget: {budget_tier}\n\n1. {gift_option_1}\n2. {gift_option_2}\n3. {gift_option_3}\n\nTop pick: {top_recommendation}\nPersonalized note: {gift_note}\nReasoning: {gift_reasoning}\n\nRequested by: {sales_rep_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1856. PHONE MOST-LIKELY-TO-ANSWER SCORING (8 nodes)
  // ===================================================================

  "Phone Most-Likely-to-Answer Scoring": {
    summary:
      "Fires on webhook when a call list is submitted. Validates phone numbers via Twilio, enriches call history with runCode, scores answer likelihood with runCode, ranks and prioritizes the call list, branches by score threshold, creates prioritized call tasks in HubSpot, and notifies the sales rep via Slack.",
    nodes: [
      trigger("Webhook: Call list submitted for scoring", "webhook_app", {
        source: "sales_dialer",
        event: "call_list_submitted",
        howItStarts:
          "Fires when a sales rep submits a call list for prioritization, triggering phone validation, answer likelihood scoring, and intelligent ranking to maximize connect rates",
        setup:
          "Configure webhook for call list submissions. Set up Twilio API for phone validation. Configure OpenAI key for AI analysis. Ensure HubSpot token has task and contact creation access. Set up Slack webhook for sales rep notifications.",
        inputFields: [],
      }),

      // 2. AI extract call list from webhook
      n(
        "2",
        "runCode",
        "Extract and parse call list from webhook payload",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload.contacts || payload.call_list || payload;
const contacts = Array.isArray(data) ? data : (data.contacts || data.leads || [data]);
output.contact_list = JSON.stringify(contacts.slice(0, 20));
output.total_contacts = contacts.length;
const first = contacts[0] || {};
output.first_phone = first.phone || first.phone_number || first.mobile || '';
output.first_name = first.name || first.full_name || (first.first_name || '') + ' ' + (first.last_name || '');
output.first_company = first.company || first.company_name || '';
output.rep_name = payload.rep_name || payload.sales_rep || payload.submitted_by || '';
output.rep_email = payload.rep_email || payload.submitter_email || '';`,
            outputVariables: [
              { name: "contact_list", type: "string" },
              { name: "total_contacts", type: "number" },
              { name: "first_phone", type: "string" },
              { name: "first_name", type: "string" },
              { name: "first_company", type: "string" },
              { name: "rep_name", type: "string" },
              { name: "rep_email", type: "string" },
            ],
          },
        },
      ),

      // 3. Validate phone numbers Twilio
      n(
        "3",
        "hitApi",
        "Twilio: Validate and lookup phone number details",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://lookups.twilio.com/v2/PhoneNumbers/{first_phone}?Fields=line_type_intelligence,caller_name",
            headers: [
              { key: "Authorization", value: "Basic {twilio_auth_base64}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "valid", customName: "phone_valid", type: "boolean" },
              {
                name: "line_type_intelligence.type",
                customName: "line_type",
                type: "string",
              },
              {
                name: "caller_name.caller_name",
                customName: "caller_name",
                type: "string",
              },
              {
                name: "national_format",
                customName: "formatted_phone",
                type: "string",
              },
              {
                name: "country_code",
                customName: "phone_country",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. Enrich call history runCode
      n(
        "4",
        "runCode",
        "Enrich call context with time zone and optimal call windows",
        760,
        {
          runCodeDetails: {
            code: `const lineType = (variables.line_type || '').toLowerCase();
const country = variables.phone_country || 'US';
const now = new Date();
const hour = now.getUTCHours();
const day = now.getUTCDay();
const tzOffsets = { US: -5, GB: 0, AU: 10, IN: 5.5, DE: 1, JP: 9 };
const offset = tzOffsets[country] || -5;
const localHour = (hour + offset + 24) % 24;
output.local_hour = localHour;
output.is_business_hours = localHour >= 9 && localHour <= 17 && day >= 1 && day <= 5 ? 'yes' : 'no';
output.optimal_window = localHour < 9 ? '9-10 AM local' : localHour > 17 ? '9-10 AM next day' : localHour + '-' + (localHour + 1) + ' local';
output.is_mobile = lineType === 'mobile' ? 'yes' : 'no';
output.line_quality = lineType === 'mobile' ? 'high' : lineType === 'landline' ? 'medium' : lineType === 'voip' ? 'low' : 'unknown';
output.call_day_quality = day >= 2 && day <= 4 ? 'optimal' : day === 1 || day === 5 ? 'good' : 'poor';`,
            outputVariables: [
              { name: "local_hour", type: "number" },
              { name: "is_business_hours", type: "string" },
              { name: "optimal_window", type: "string" },
              { name: "is_mobile", type: "string" },
              { name: "line_quality", type: "string" },
              { name: "call_day_quality", type: "string" },
            ],
          },
        },
      ),

      // 5. Score answer likelihood runCode
      n(
        "5",
        "runCode",
        "Score contact answer likelihood based on all signals",
        1000,
        {
          runCodeDetails: {
            code: `let score = 50;
const isMobile = variables.is_mobile === 'yes';
const isBusinessHours = variables.is_business_hours === 'yes';
const lineQuality = (variables.line_quality || '').toLowerCase();
const dayQuality = (variables.call_day_quality || '').toLowerCase();
const phoneValid = variables.phone_valid === true || variables.phone_valid === 'true';
if (!phoneValid) { score = 0; } else {
  if (isMobile) score += 20; else if (lineQuality === 'medium') score += 10;
  if (isBusinessHours) score += 15; else score -= 10;
  if (dayQuality === 'optimal') score += 10; else if (dayQuality === 'good') score += 5; else score -= 5;
  if (lineQuality === 'low') score -= 15;
}
output.answer_score = Math.max(0, Math.min(100, score));
output.answer_tier = score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low';
output.call_recommendation = score >= 70 ? 'Call now — high connect probability' : score >= 40 ? 'Schedule for optimal window: ' + variables.optimal_window : 'Deprioritize — try email first';
output.best_time = variables.optimal_window;`,
            outputVariables: [
              { name: "answer_score", type: "number" },
              { name: "answer_tier", type: "string" },
              { name: "call_recommendation", type: "string" },
              { name: "best_time", type: "string" },
            ],
          },
        },
      ),

      // 6. Branch by score threshold condition
      n("6", "condition", "Route by answer likelihood score threshold", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "answer_score", operator: "greater_than", value: "39" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Create prioritized call tasks HubSpot (high/medium path)
      hubspotPostNode(
        "7",
        "HubSpot: Create prioritized call task for sales rep",
        1480,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"Call {first_name} at {first_company} — Score: {answer_score}/100","hs_task_body":"Phone: {formatted_phone} | Line: {line_type} | Mobile: {is_mobile} | Score: {answer_score} ({answer_tier}) | Recommendation: {call_recommendation} | Best time: {best_time} | Business hours: {is_business_hours}","hs_task_priority":"HIGH","hs_task_type":"CALL","hubspot_owner_id":"{owner_id}"}',
          },
        ],
        [
          { name: "id", customName: "task_id", type: "string" },
          {
            name: "properties.hs_task_subject",
            customName: "task_subject",
            type: "string",
          },
        ],
      ),

      // 8. Notify sales rep Slack
      slackNode(
        "8",
        "Slack: Notify sales rep with prioritized call list",
        1720,
        "Call List Scored & Prioritized:\n- Contact: {first_name} at {first_company}\n- Phone: {formatted_phone} ({line_type})\n- Answer score: {answer_score}/100 ({answer_tier})\n- Recommendation: {call_recommendation}\n- Best time: {best_time}\n- Business hours now: {is_business_hours}\n- Task created: {task_subject}\n- Total contacts in list: {total_contacts}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1857. PIPEDRIVE ENRICHMENT BATCH (7 nodes)
  // ===================================================================

  "Pipedrive Enrichment Batch": {
    summary:
      "Runs on schedule to fetch unenriched contacts from Pipedrive API, enriches them via Clearbit, validates enrichment quality with runCode, branches by data completeness, writes enriched fields back to Pipedrive, and sends an enrichment summary to Google Sheets.",
    nodes: [
      trigger("Schedule: Daily Pipedrive contact enrichment", "schedule", {
        schedule: "Every day at 6:00 AM",
        howItStarts:
          "Fires daily to batch-enrich Pipedrive contacts that are missing key firmographic and demographic data, using Clearbit for enrichment and writing results back to Pipedrive",
        setup:
          "Configure Pipedrive API key for contact read/write access. Set up Clearbit API key for enrichment. Configure Google Sheets API for enrichment reporting. Provide Google token for Sheets access.",
        inputFields: [
          { name: "enrichment_batch_size", type: "number", example: "25" },
        ],
      }),

      // 2. Fetch unenriched contacts from Pipedrive
      n(
        "2",
        "hitApi",
        "Pipedrive: Fetch contacts missing enrichment data",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.pipedrive.com/v1/persons?api_token={pipedrive_key}&limit={enrichment_batch_size}&filter_id={unenriched_filter_id}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              { name: "data[0].id", customName: "person_id", type: "string" },
              {
                name: "data[0].primary_email",
                customName: "contact_email",
                type: "string",
              },
              {
                name: "data[0].name",
                customName: "contact_name",
                type: "string",
              },
              {
                name: "data[0].org_id.name",
                customName: "org_name",
                type: "string",
              },
              {
                name: "additional_data.pagination.more_items_in_collection",
                customName: "has_more",
                type: "boolean",
              },
              {
                name: "additional_data.pagination.next_start",
                customName: "next_start",
                type: "number",
              },
            ],
          },
        },
      ),

      // 3. Enrich via Clearbit
      n("3", "hitApi", "Clearbit: Enrich contact with firmographic data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person-stream.clearbit.com/v2/combined/find?email={contact_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "person.employment.title",
              customName: "enriched_title",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "enriched_seniority",
              type: "string",
            },
            {
              name: "person.employment.role",
              customName: "enriched_role",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "enriched_industry",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "enriched_employees",
              type: "number",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "enriched_revenue",
              type: "number",
            },
            {
              name: "company.domain",
              customName: "enriched_domain",
              type: "string",
            },
            {
              name: "person.location",
              customName: "enriched_location",
              type: "string",
            },
          ],
        },
      }),

      // 4. Validate enrichment quality runCode
      n(
        "4",
        "runCode",
        "Validate enrichment data completeness and quality",
        760,
        {
          runCodeDetails: {
            code: `const fields = {
  title: variables.enriched_title || '',
  seniority: variables.enriched_seniority || '',
  role: variables.enriched_role || '',
  company: variables.enriched_company || '',
  industry: variables.enriched_industry || '',
  employees: variables.enriched_employees || '',
  revenue: variables.enriched_revenue || '',
  domain: variables.enriched_domain || '',
  location: variables.enriched_location || '',
};
let filled = 0;
let total = Object.keys(fields).length;
for (const [k, v] of Object.entries(fields)) { if (v && v !== '0' && v !== 'null') filled++; }
output.completeness_score = Math.round((filled / total) * 100);
output.completeness_level = output.completeness_score >= 70 ? 'high' : output.completeness_score >= 40 ? 'medium' : 'low';
output.missing_fields = Object.entries(fields).filter(([k, v]) => !v || v === '0' || v === 'null').map(([k]) => k).join(', ');
output.enriched_field_count = filled;
output.total_field_count = total;
output.should_write_back = output.completeness_score >= 30 ? 'yes' : 'no';`,
            outputVariables: [
              { name: "completeness_score", type: "number" },
              { name: "completeness_level", type: "string" },
              { name: "missing_fields", type: "string" },
              { name: "enriched_field_count", type: "number" },
              { name: "total_field_count", type: "number" },
              { name: "should_write_back", type: "string" },
            ],
          },
        },
      ),

      // 5. Branch by completeness condition
      n(
        "5",
        "condition",
        "Check if enrichment data meets quality threshold",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "should_write_back",
                operator: "equals",
                value: "yes",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. Write enriched fields back to Pipedrive (pass path)
      n("6", "hitApi", "Pipedrive: Update contact with enriched data", 1240, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.pipedrive.com/v1/persons/{person_id}?api_token={pipedrive_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "job_title", value: "{enriched_title}" },
            { key: "org_name", value: "{enriched_company}" },
            { key: "label", value: "{enriched_seniority}" },
          ],
          variables: [
            { name: "success", customName: "update_success", type: "boolean" },
            {
              name: "data.id",
              customName: "updated_person_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Send enrichment summary Sheets
      sheetsNode(
        "7",
        "Google Sheets: Log enrichment batch summary",
        1480,
        "EnrichmentLog",
        '[["{contact_name}","{contact_email}","{enriched_company}","{enriched_title}","{enriched_industry}","{enriched_employees}","{completeness_score}","{completeness_level}","{missing_fields}","{update_success}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1858. PODCAST GUEST PITCH (9 nodes)
  // ===================================================================

  "Podcast Guest Pitch": {
    summary:
      "Fires on manual trigger for a podcast guest campaign. AI researches podcast details, analyzes episode-content fit, fetches host contact via API, generates a guest pitch, branches by podcast size, sends the pitch email, creates a booking pipeline entry in Google Sheets, and schedules a follow-up delay.",
    nodes: [
      trigger("Manual: Launch podcast guest pitch campaign", "manual", {
        howItStarts:
          "Manually triggered to research target podcasts, analyze content fit, discover host contacts, and generate personalized guest pitches for thought leadership outreach",
        setup:
          "Configure ListenNotes API key for podcast research. Set up Hunter.io API for host contact discovery. Configure OpenAI key for AI pitch generation. Provide Google Sheets token for booking pipeline. Set up email sending for pitch delivery.",
        inputFields: [
          { name: "podcast_name", type: "string", example: "The SaaS Podcast" },
          {
            name: "podcast_url",
            type: "string",
            example: "https://www.thesaaspodcast.com",
          },
          { name: "guest_name", type: "string", example: "Jane Smith" },
          { name: "guest_title", type: "string", example: "CEO, OmniEngage" },
          {
            name: "guest_expertise",
            type: "string",
            example: "Product-led growth, B2B marketing automation",
          },
        ],
      }),

      // 2. AI research podcast details
      aiNode(
        "2",
        "AI research podcast format, audience, and recent episodes",
        280,
        "You are a podcast research analyst. Given a podcast name and URL, research the podcast's format, typical episode length, audience demographics, popular topics, and recent guest types. Return JSON: {podcast_format, episode_length, audience_size_estimate, audience_type, popular_topics, recent_guest_types, podcast_niche, monetization_model}",
        "Research podcast: {podcast_name} (URL: {podcast_url}) | Target guest: {guest_name}, {guest_title} | Expertise: {guest_expertise}",
        [
          { field: "podcast_format", as: "podcast_format", type: "string" },
          { field: "episode_length", as: "episode_length", type: "string" },
          {
            field: "audience_size_estimate",
            as: "audience_size",
            type: "string",
          },
          { field: "audience_type", as: "audience_type", type: "string" },
          { field: "popular_topics", as: "popular_topics", type: "string" },
          {
            field: "recent_guest_types",
            as: "recent_guest_types",
            type: "string",
          },
          { field: "podcast_niche", as: "podcast_niche", type: "string" },
          {
            field: "monetization_model",
            as: "monetization_model",
            type: "string",
          },
        ],
      ),

      // 3. AI analyze episode-content fit
      aiNode(
        "3",
        "AI analyze content fit between guest expertise and podcast",
        520,
        "You are a podcast booking strategist. Analyze the fit between the guest's expertise and the podcast's audience and topics. Suggest 3 specific episode topic ideas that would resonate. Score the fit. Return JSON: {fit_score, fit_reasoning, topic_idea_1, topic_idea_2, topic_idea_3, unique_angle, audience_value_prop, potential_reach}",
        "Guest: {guest_name}, {guest_title} | Expertise: {guest_expertise} | Podcast: {podcast_name} | Format: {podcast_format} | Audience: {audience_type} ({audience_size}) | Topics: {popular_topics} | Recent guests: {recent_guest_types} | Niche: {podcast_niche}",
        [
          { field: "fit_score", as: "content_fit_score", type: "number" },
          { field: "fit_reasoning", as: "fit_reasoning", type: "string" },
          { field: "topic_idea_1", as: "topic_idea_1", type: "string" },
          { field: "topic_idea_2", as: "topic_idea_2", type: "string" },
          { field: "topic_idea_3", as: "topic_idea_3", type: "string" },
          { field: "unique_angle", as: "unique_angle", type: "string" },
          {
            field: "audience_value_prop",
            as: "audience_value_prop",
            type: "string",
          },
          { field: "potential_reach", as: "potential_reach", type: "string" },
        ],
      ),

      // 4. Fetch host contact via Hunter.io
      n(
        "4",
        "hitApi",
        "Hunter.io: Find podcast host contact information",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hunter.io/v2/domain-search?domain={podcast_url}&api_key={hunter_api_key}&limit=3",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "data.emails[0].value",
                customName: "host_email",
                type: "string",
              },
              {
                name: "data.emails[0].first_name",
                customName: "host_first_name",
                type: "string",
              },
              {
                name: "data.emails[0].last_name",
                customName: "host_last_name",
                type: "string",
              },
              {
                name: "data.emails[0].position",
                customName: "host_title",
                type: "string",
              },
            ],
          },
        },
      ),

      // 5. AI generate guest pitch
      aiNode(
        "5",
        "AI generate personalized podcast guest pitch email",
        1000,
        "You are a podcast PR specialist who writes compelling guest pitches. Write a concise, personalized pitch that references specific episodes or topics the podcast covers, proposes concrete episode ideas, and highlights what the guest uniquely brings to the audience. Return JSON: {subject_line, pitch_body, hook_line, proposed_topics_summary, guest_bio_snippet}",
        "Host: {host_first_name} {host_last_name} | Podcast: {podcast_name} | Guest: {guest_name}, {guest_title} | Expertise: {guest_expertise} | Fit: {content_fit_score}/100 | Topic 1: {topic_idea_1} | Topic 2: {topic_idea_2} | Topic 3: {topic_idea_3} | Unique angle: {unique_angle} | Audience value: {audience_value_prop}",
        [
          { field: "subject_line", as: "pitch_subject", type: "string" },
          { field: "pitch_body", as: "pitch_body", type: "string" },
          { field: "hook_line", as: "pitch_hook", type: "string" },
          {
            field: "proposed_topics_summary",
            as: "topics_summary",
            type: "string",
          },
          { field: "guest_bio_snippet", as: "guest_bio", type: "string" },
        ],
      ),

      // 6. Branch by podcast size condition
      n(
        "6",
        "condition",
        "Route by podcast audience size for outreach priority",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "content_fit_score",
                operator: "greater_than",
                value: "60",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Send pitch email (high-fit path)
      emailNode(
        "7",
        "Send personalized guest pitch to podcast host",
        1480,
        "{host_email}",
        "{pitch_subject}",
        "{pitch_body}",
      ),

      // 8. Create booking pipeline Sheets
      sheetsNode(
        "8",
        "Google Sheets: Log podcast pitch in booking pipeline",
        1720,
        "PodcastPipeline",
        '[["{podcast_name}","{host_first_name} {host_last_name}","{host_email}","{content_fit_score}","{topic_idea_1}","{pitch_subject}","{audience_size}","{podcast_niche}",""]]',
      ),

      // 9. Schedule follow-up delay
      n("9", "delayAction", "Wait 5 days before follow-up", 1960, {
        delayDetails: { duration: 5, unit: "days" },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1859. POSITIVE PRODUCT ADOPTION PITCH (8 nodes)
  // ===================================================================

  "Positive Product Adoption Pitch": {
    summary:
      "Fires on webhook when a positive product adoption signal is detected. Fetches the prospect company profile from Clearbit, uses AI to analyze tool synergy, scores pitch timeliness with runCode, generates an AI adoption-aware pitch, branches by signal strength, sends the email, and logs the signal in HubSpot CRM.",
    nodes: [
      trigger(
        "Webhook: Positive product adoption signal detected",
        "webhook_app",
        {
          source: "product_intelligence",
          event: "adoption_signal",
          howItStarts:
            "Fires when a prospect company shows positive adoption of a complementary or related tool, signaling readiness for your solution pitch based on their expanding tech stack",
          setup:
            "Configure product intelligence webhook (e.g., BuiltWith, G2, or HG Insights events). Set up Clearbit API key for company enrichment. Configure OpenAI key for AI analysis and pitch generation. Ensure HubSpot token has contact and deal creation access.",
          inputFields: [],
        },
      ),

      // 2. AI extract adoption signal from webhook
      aiNode(
        "2",
        "AI extract product adoption signal details from webhook",
        280,
        "You are a data extraction specialist for product intelligence events. Extract the adoption signal details regardless of format. Find the company name, domain, adopted product/tool, adoption stage, signal source, and any other context. Return JSON: {company_name, company_domain, adopted_product, adoption_stage, signal_source, adoption_date, category, additional_context}",
        "Extract adoption signal details from: {webhook_payload}",
        [
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
          { field: "adopted_product", as: "adopted_product", type: "string" },
          { field: "adoption_stage", as: "adoption_stage", type: "string" },
          { field: "signal_source", as: "signal_source", type: "string" },
          { field: "adoption_date", as: "adoption_date", type: "string" },
          { field: "category", as: "product_category", type: "string" },
          { field: "additional_context", as: "signal_context", type: "string" },
        ],
      ),

      // 3. Fetch prospect company profile Clearbit
      n(
        "3",
        "hitApi",
        "Clearbit: Fetch prospect company profile and firmographics",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company-stream.clearbit.com/v2/companies/find?domain={company_domain}",
            headers: [
              { key: "Authorization", value: "Bearer {clearbit_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "name", customName: "enriched_company", type: "string" },
              {
                name: "category.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "metrics.employees",
                customName: "employee_count",
                type: "number",
              },
              {
                name: "metrics.annualRevenue",
                customName: "annual_revenue",
                type: "number",
              },
              {
                name: "tech",
                customName: "current_tech_stack",
                type: "string",
              },
              {
                name: "category.subIndustry",
                customName: "sub_industry",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. AI analyze tool synergy
      aiNode(
        "4",
        "AI analyze synergy between adopted product and your solution",
        760,
        "You are a product ecosystem analyst. Given a company's newly adopted product and their existing tech stack, analyze the synergy opportunity with our solution. Identify integration touchpoints, workflow improvements, and the natural next step in their tech stack evolution. Return JSON: {synergy_score, synergy_reasoning, integration_points, workflow_improvement, value_multiplier, competitor_risk, timing_assessment}",
        "Company: {enriched_company} | Industry: {industry} ({sub_industry}) | Employees: {employee_count} | Adopted: {adopted_product} ({product_category}) | Stage: {adoption_stage} | Existing tech: {current_tech_stack} | Signal: {signal_context}",
        [
          { field: "synergy_score", as: "synergy_score", type: "number" },
          {
            field: "synergy_reasoning",
            as: "synergy_reasoning",
            type: "string",
          },
          {
            field: "integration_points",
            as: "integration_points",
            type: "string",
          },
          {
            field: "workflow_improvement",
            as: "workflow_improvement",
            type: "string",
          },
          { field: "value_multiplier", as: "value_multiplier", type: "string" },
          { field: "competitor_risk", as: "competitor_risk", type: "string" },
          {
            field: "timing_assessment",
            as: "timing_assessment",
            type: "string",
          },
        ],
      ),

      // 5. Score pitch timeliness runCode
      n(
        "5",
        "runCode",
        "Score pitch timeliness based on adoption stage and synergy",
        1000,
        {
          runCodeDetails: {
            code: `let score = 0;
const stage = (variables.adoption_stage || '').toLowerCase();
const synergy = parseInt(variables.synergy_score) || 0;
const empCount = parseInt(variables.employee_count) || 0;
const competitorRisk = (variables.competitor_risk || '').toLowerCase();
if (stage.includes('evaluating') || stage.includes('trial')) score += 30;
else if (stage.includes('recent') || stage.includes('new')) score += 25;
else if (stage.includes('active') || stage.includes('deployed')) score += 15;
score += Math.round(synergy * 0.3);
if (empCount >= 50 && empCount <= 5000) score += 15;
else if (empCount > 5000) score += 10;
if (competitorRisk.includes('high')) score += 10;
output.signal_strength = Math.min(100, score);
output.signal_tier = score >= 70 ? 'strong' : score >= 40 ? 'moderate' : 'weak';
output.pitch_urgency = score >= 70 ? 'immediate' : score >= 40 ? 'this_week' : 'nurture';
output.recommended_approach = score >= 70 ? 'Direct outreach with integration pitch' : score >= 40 ? 'Educational content about complementary value' : 'Add to nurture sequence';`,
            outputVariables: [
              { name: "signal_strength", type: "number" },
              { name: "signal_tier", type: "string" },
              { name: "pitch_urgency", type: "string" },
              { name: "recommended_approach", type: "string" },
            ],
          },
        },
      ),

      // 6. AI adoption-aware pitch
      aiNode(
        "6",
        "AI generate adoption-aware personalized pitch email",
        1240,
        "You are a consultative B2B sales writer. Write an email that congratulates the prospect on their recent product adoption, naturally bridges to how your solution integrates with and amplifies the value of their new tool, and offers a concrete next step. Do not be pushy — position as a helpful partner. Return JSON: {subject_line, email_body, adoption_reference, integration_hook, cta_text}",
        "Company: {enriched_company} | Industry: {industry} | Adopted: {adopted_product} ({product_category}) | Stage: {adoption_stage} | Synergy: {synergy_score}/100 | Integration points: {integration_points} | Workflow improvement: {workflow_improvement} | Value multiplier: {value_multiplier} | Approach: {recommended_approach} | Signal: {signal_tier}",
        [
          { field: "subject_line", as: "adoption_subject", type: "string" },
          { field: "email_body", as: "adoption_body", type: "string" },
          {
            field: "adoption_reference",
            as: "adoption_reference",
            type: "string",
          },
          { field: "integration_hook", as: "integration_hook", type: "string" },
          { field: "cta_text", as: "adoption_cta", type: "string" },
        ],
      ),

      // 7. Branch by signal strength condition
      n(
        "7",
        "condition",
        "Route by signal strength for outreach channel",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "signal_strength",
                operator: "greater_than",
                value: "39",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8. Send email + log CRM
      emailNode(
        "8",
        "Send adoption-aware pitch email",
        1720,
        "{company_domain}",
        "{adoption_subject}",
        "{adoption_body}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1860. POSITIVE REPLY ROUTER (8 nodes)
  // ===================================================================

  "Positive Reply Router": {
    summary:
      "Fires on webhook when an email reply is received. Parses reply content with runCode, uses AI to classify reply intent, extracts key details with runCode, branches by classification (positive vs objection), handles positive replies with runCode, handles objections with AI, and updates CRM plus notifies the rep via HubSpot and Slack.",
    nodes: [
      trigger("Webhook: Email reply received", "webhook_app", {
        source: "email_platform",
        event: "reply_received",
        howItStarts:
          "Fires when a prospect replies to an outbound email, triggering intelligent classification, routing, and response preparation based on reply intent",
        setup:
          "Configure email platform webhook for reply events (e.g., Postmark inbound, SendGrid inbound parse). Configure OpenAI key for AI classification. Ensure HubSpot token has contact and deal update access. Set up Slack webhook for sales team notifications.",
        inputFields: [],
      }),

      // 2. Parse reply content runCode
      n(
        "2",
        "runCode",
        "Parse and clean email reply content from webhook",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
output.reply_from = data.from || data.From || data.sender || data.from_email || '';
output.reply_subject = data.subject || data.Subject || '';
output.reply_body = (data.text || data.TextBody || data.body || data.html_body || '').replace(/<[^>]+>/g, '').trim();
output.reply_date = data.date || data.Date || new Date().toISOString();
output.original_to = data.to || data.To || data.recipient || '';
output.in_reply_to = data.in_reply_to || data.InReplyTo || data.message_id || '';
output.reply_length = output.reply_body.length;
output.has_attachment = (data.attachments || data.Attachments || []).length > 0 ? 'yes' : 'no';
const body = output.reply_body.toLowerCase();
output.quick_sentiment = body.includes('interested') || body.includes('love to') || body.includes('sounds great') || body.includes('let') ? 'positive' : body.includes('not interested') || body.includes('unsubscribe') || body.includes('remove') ? 'negative' : 'neutral';`,
            outputVariables: [
              { name: "reply_from", type: "string" },
              { name: "reply_subject", type: "string" },
              { name: "reply_body", type: "string" },
              { name: "reply_date", type: "string" },
              { name: "original_to", type: "string" },
              { name: "in_reply_to", type: "string" },
              { name: "reply_length", type: "number" },
              { name: "has_attachment", type: "string" },
              { name: "quick_sentiment", type: "string" },
            ],
          },
        },
      ),

      // 3. AI classify reply intent
      aiNode(
        "3",
        "AI classify reply intent and sentiment",
        520,
        "You are an expert sales reply analyst. Classify this email reply into one of these categories: 'positive_interest' (wants to learn more, book a meeting), 'objection' (price concern, timing issue, not the right person), 'referral' (redirecting to someone else), 'unsubscribe' (wants to be removed), 'out_of_office' (auto-reply), 'question' (asking for more information), or 'other'. Also assess urgency and next best action. Return JSON: {classification, confidence, sentiment_score, urgency, key_phrases, objection_type, next_best_action, recommended_response_tone}",
        "Reply from: {reply_from} | Subject: {reply_subject} | Body: {reply_body} | Quick sentiment: {quick_sentiment} | Has attachment: {has_attachment}",
        [
          {
            field: "classification",
            as: "reply_classification",
            type: "string",
          },
          {
            field: "confidence",
            as: "classification_confidence",
            type: "number",
          },
          { field: "sentiment_score", as: "sentiment_score", type: "number" },
          { field: "urgency", as: "reply_urgency", type: "string" },
          { field: "key_phrases", as: "key_phrases", type: "string" },
          { field: "objection_type", as: "objection_type", type: "string" },
          { field: "next_best_action", as: "next_best_action", type: "string" },
          {
            field: "recommended_response_tone",
            as: "response_tone",
            type: "string",
          },
        ],
      ),

      // 4. Extract key details runCode
      n(
        "4",
        "runCode",
        "Extract actionable details from classified reply",
        760,
        {
          runCodeDetails: {
            code: `const classification = (variables.reply_classification || '').toLowerCase();
const body = variables.reply_body || '';
output.is_positive = ['positive_interest','question','referral'].includes(classification) ? 'yes' : 'no';
output.is_objection = classification === 'objection' ? 'yes' : 'no';
output.needs_human = ['positive_interest','objection','referral'].includes(classification) ? 'yes' : 'no';
const emailMatch = body.match(/[\\w.-]+@[\\w.-]+\\.\\w+/);
output.referred_email = emailMatch ? emailMatch[0] : '';
const phoneMatch = body.match(/\\+?[\\d\\s.-]{10,}/);
output.mentioned_phone = phoneMatch ? phoneMatch[0].trim() : '';
const timeMatch = body.match(/(monday|tuesday|wednesday|thursday|friday|next week|tomorrow|this week|\\d{1,2}[:/]\\d{2})/i);
output.suggested_time = timeMatch ? timeMatch[0] : '';
output.routing_action = classification === 'positive_interest' ? 'book_meeting' : classification === 'objection' ? 'handle_objection' : classification === 'referral' ? 'redirect_outreach' : classification === 'unsubscribe' ? 'remove_from_sequence' : 'review_manually';`,
            outputVariables: [
              { name: "is_positive", type: "string" },
              { name: "is_objection", type: "string" },
              { name: "needs_human", type: "string" },
              { name: "referred_email", type: "string" },
              { name: "mentioned_phone", type: "string" },
              { name: "suggested_time", type: "string" },
              { name: "routing_action", type: "string" },
            ],
          },
        },
      ),

      // 5. Branch by classification condition
      n(
        "5",
        "condition",
        "Route by reply classification: positive vs objection",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "is_positive", operator: "equals", value: "yes" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. Handle positive replies runCode (positive path)
      n(
        "6",
        "runCode",
        "Prepare positive reply response and meeting booking data",
        1240,
        {
          runCodeDetails: {
            code: `output.meeting_subject = 'Follow-up: ' + variables.reply_subject;
output.meeting_notes = 'Positive reply from ' + variables.reply_from + '. Key phrases: ' + variables.key_phrases + '. Suggested time: ' + (variables.suggested_time || 'TBD') + '. Urgency: ' + variables.reply_urgency;
output.response_priority = variables.reply_urgency === 'high' ? 'respond_within_1hr' : 'respond_within_4hrs';
output.crm_note = 'POSITIVE REPLY | Classification: ' + variables.reply_classification + ' (confidence: ' + variables.classification_confidence + '%) | Action: ' + variables.routing_action + ' | Next: ' + variables.next_best_action;`,
            outputVariables: [
              { name: "meeting_subject", type: "string" },
              { name: "meeting_notes", type: "string" },
              { name: "response_priority", type: "string" },
              { name: "crm_note", type: "string" },
            ],
          },
        },
      ),

      // 7. Handle objection replies AI (objection path)
      aiNode(
        "7",
        "AI draft objection handling response",
        1240,
        "You are a sales objection handling expert. Given a prospect's objection, draft a thoughtful response that acknowledges their concern, provides relevant information to address it, and keeps the conversation going without being pushy. Return JSON: {response_subject, response_body, objection_addressed, alternative_offer, crm_note}",
        "Reply from: {reply_from} | Objection type: {objection_type} | Reply body: {reply_body} | Tone to use: {response_tone} | Next best action: {next_best_action} | Key phrases: {key_phrases}",
        [
          {
            field: "response_subject",
            as: "objection_response_subject",
            type: "string",
          },
          {
            field: "response_body",
            as: "objection_response_body",
            type: "string",
          },
          {
            field: "objection_addressed",
            as: "objection_addressed",
            type: "string",
          },
          {
            field: "alternative_offer",
            as: "alternative_offer",
            type: "string",
          },
          { field: "crm_note", as: "objection_crm_note", type: "string" },
        ],
        380,
      ),

      // 8. Update CRM + notify rep HubSpot Slack
      slackNode(
        "8",
        "Slack: Notify rep of classified reply and recommended action",
        1480,
        "Email Reply Classified:\n- From: {reply_from}\n- Classification: {reply_classification} (confidence: {classification_confidence}%)\n- Sentiment: {sentiment_score}/100\n- Urgency: {reply_urgency}\n- Key phrases: {key_phrases}\n- Routing: {routing_action}\n- Suggested time: {suggested_time}\n- Next action: {next_best_action}\n- Response priority: {response_priority}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1861. POST-DEMO TECH EVALUATOR FOLLOW-UP (8 nodes)
  // ===================================================================

  "Post-Demo Tech Evaluator Follow-Up": {
    summary:
      "Fires on webhook when a demo is completed. Fetches the evaluator profile from HubSpot, uses AI to identify relevant technical resources, generates a personalized technical follow-up email, compiles a resource package with runCode, branches by evaluator seniority, sends the follow-up email, and creates a follow-up task in HubSpot.",
    nodes: [
      trigger("Webhook: Demo completed for tech evaluator", "webhook_app", {
        source: "demo_platform",
        event: "demo_completed",
        howItStarts:
          "Fires when a product demo is completed with a technical evaluator, triggering a personalized follow-up with relevant technical resources and documentation tailored to their evaluation criteria",
        setup:
          "Configure demo platform webhook for demo completion events (e.g., Calendly, Demodesk, or custom events). Ensure HubSpot token has contact and task access. Configure OpenAI key for AI resource matching and content generation.",
        inputFields: [],
      }),

      // 2. AI extract demo details from webhook
      aiNode(
        "2",
        "AI extract demo completion details from webhook payload",
        280,
        "You are a data extraction specialist. Extract the demo completion details from this webhook payload regardless of format. Find the evaluator email, name, company, role/title, demo topics covered, questions asked, features demonstrated, and demo duration. Return JSON: {evaluator_email, evaluator_name, evaluator_company, evaluator_title, demo_topics, questions_asked, features_shown, demo_duration, demo_date, sales_engineer}",
        "Extract demo details from: {webhook_payload}",
        [
          { field: "evaluator_email", as: "evaluator_email", type: "string" },
          { field: "evaluator_name", as: "evaluator_name", type: "string" },
          {
            field: "evaluator_company",
            as: "evaluator_company",
            type: "string",
          },
          { field: "evaluator_title", as: "evaluator_title", type: "string" },
          { field: "demo_topics", as: "demo_topics", type: "string" },
          { field: "questions_asked", as: "questions_asked", type: "string" },
          { field: "features_shown", as: "features_shown", type: "string" },
          { field: "demo_duration", as: "demo_duration", type: "string" },
          { field: "demo_date", as: "demo_date", type: "string" },
          { field: "sales_engineer", as: "sales_engineer", type: "string" },
        ],
      ),

      // 3. Fetch evaluator profile HubSpot
      hubspotGetNode(
        "3",
        "HubSpot: Fetch evaluator contact profile and deal context",
        520,
        "crm/v3/objects/contacts/{evaluator_email}?idProperty=email&properties=firstname,lastname,jobtitle,company,lifecyclestage,hs_lead_status,num_associated_deals,notes_last_updated",
        [
          {
            name: "properties.firstname",
            customName: "contact_first",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "contact_last",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "crm_title",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "associated_deals",
            type: "number",
          },
          { name: "id", customName: "hubspot_contact_id", type: "string" },
        ],
      ),

      // 4. AI identify relevant resources
      aiNode(
        "4",
        "AI identify relevant technical resources for follow-up",
        760,
        "You are a technical pre-sales resource specialist. Based on the demo topics, questions asked, and evaluator's role, identify the most relevant technical resources to include in follow-up. Return JSON: {resource_1_title, resource_1_url_hint, resource_1_relevance, resource_2_title, resource_2_url_hint, resource_2_relevance, resource_3_title, resource_3_url_hint, resource_3_relevance, technical_depth_level, evaluation_stage_guess}",
        "Evaluator: {evaluator_name}, {evaluator_title} at {evaluator_company} | Demo topics: {demo_topics} | Questions: {questions_asked} | Features shown: {features_shown} | Duration: {demo_duration} | Lifecycle: {lifecycle_stage}",
        [
          { field: "resource_1_title", as: "resource_1_title", type: "string" },
          {
            field: "resource_1_url_hint",
            as: "resource_1_url",
            type: "string",
          },
          {
            field: "resource_1_relevance",
            as: "resource_1_why",
            type: "string",
          },
          { field: "resource_2_title", as: "resource_2_title", type: "string" },
          {
            field: "resource_2_url_hint",
            as: "resource_2_url",
            type: "string",
          },
          { field: "resource_3_title", as: "resource_3_title", type: "string" },
          {
            field: "resource_3_url_hint",
            as: "resource_3_url",
            type: "string",
          },
          { field: "technical_depth_level", as: "tech_depth", type: "string" },
          { field: "evaluation_stage_guess", as: "eval_stage", type: "string" },
        ],
      ),

      // 5. AI personalized technical follow-up
      aiNode(
        "5",
        "AI generate personalized technical follow-up email",
        1000,
        "You are a technical pre-sales writer. Write a follow-up email for a technical evaluator after a product demo. Reference specific topics discussed, answer any outstanding questions, include links to relevant resources, and propose a clear next step (e.g., sandbox access, architecture review, POC). Be technical and direct. Return JSON: {subject_line, email_body, key_takeaway, next_step_proposal}",
        "Evaluator: {contact_first} {contact_last}, {crm_title} at {evaluator_company} | Demo topics: {demo_topics} | Questions: {questions_asked} | Features: {features_shown} | Resources: 1) {resource_1_title} - {resource_1_why}, 2) {resource_2_title}, 3) {resource_3_title} | Tech depth: {tech_depth} | Eval stage: {eval_stage} | SE: {sales_engineer}",
        [
          { field: "subject_line", as: "followup_subject", type: "string" },
          { field: "email_body", as: "followup_body", type: "string" },
          { field: "key_takeaway", as: "key_takeaway", type: "string" },
          { field: "next_step_proposal", as: "next_step", type: "string" },
        ],
      ),

      // 6. Compile resource package runCode
      n(
        "6",
        "runCode",
        "Compile resource package and determine evaluator seniority",
        1240,
        {
          runCodeDetails: {
            code: `output.resource_package = '1. ' + variables.resource_1_title + ' (' + variables.resource_1_url + ')\\n2. ' + variables.resource_2_title + ' (' + variables.resource_2_url + ')\\n3. ' + variables.resource_3_title + ' (' + variables.resource_3_url + ')';
const title = (variables.crm_title || variables.evaluator_title || '').toLowerCase();
const seniorTitles = ['director','vp','vice president','head of','principal','chief','senior architect','staff engineer','distinguished'];
const isSenior = seniorTitles.some(t => title.includes(t));
output.evaluator_seniority = isSenior ? 'senior' : 'standard';
output.followup_priority = isSenior ? 'high' : 'normal';
output.task_due_days = isSenior ? 1 : 3;`,
            outputVariables: [
              { name: "resource_package", type: "string" },
              { name: "evaluator_seniority", type: "string" },
              { name: "followup_priority", type: "string" },
              { name: "task_due_days", type: "number" },
            ],
          },
        },
      ),

      // 7. Branch by evaluator seniority condition
      n(
        "7",
        "condition",
        "Route by evaluator seniority for follow-up priority",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "evaluator_seniority",
                operator: "equals",
                value: "senior",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8. Send follow-up email + create task
      emailNode(
        "8",
        "Send personalized technical follow-up email",
        1720,
        "{evaluator_email}",
        "{followup_subject}",
        "{followup_body}\n\nResources:\n{resource_package}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1862. POST-MEETING ACCOUNT PLAN (9 nodes)
  // ===================================================================

  "Post-Meeting Account Plan": {
    summary:
      "Fires on webhook when a meeting is completed. Fetches meeting notes via API, pulls account history from HubSpot, generates an AI strategic account plan, builds a stakeholder influence map with runCode, branches by deal size, saves the plan to HubSpot CRM, shares it via Google Docs API, and notifies the AE and leadership via Slack.",
    nodes: [
      trigger("Webhook: Meeting completed with prospect", "webhook_app", {
        source: "calendar_platform",
        event: "meeting_completed",
        howItStarts:
          "Fires when a prospect meeting is completed, triggering automatic generation of a strategic account plan with stakeholder mapping, next steps, and competitive positioning",
        setup:
          "Configure calendar/meeting platform webhook for meeting completion events (e.g., Calendly, Gong, or custom events). Set up meeting notes API or transcription service. Ensure HubSpot token has contact, company, and deal access. Configure OpenAI key for AI planning. Set up Google Docs API for document sharing. Set up Slack webhook for AE and leadership notifications.",
        inputFields: [],
      }),

      // 2. AI extract meeting details from webhook
      aiNode(
        "2",
        "AI extract meeting completion details from webhook payload",
        280,
        "You are a data extraction specialist. Extract meeting details from this webhook payload regardless of format. Find the meeting participants, company, meeting type, duration, any notes or transcript summary, and next steps discussed. Return JSON: {prospect_email, prospect_name, prospect_company, prospect_title, meeting_type, meeting_duration, meeting_notes_summary, next_steps_discussed, attendees, ae_name, ae_email}",
        "Extract meeting details from: {webhook_payload}",
        [
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "prospect_title", as: "prospect_title", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
          { field: "meeting_duration", as: "meeting_duration", type: "string" },
          {
            field: "meeting_notes_summary",
            as: "meeting_notes",
            type: "string",
          },
          {
            field: "next_steps_discussed",
            as: "next_steps_raw",
            type: "string",
          },
          { field: "attendees", as: "attendees", type: "string" },
          { field: "ae_name", as: "ae_name", type: "string" },
          { field: "ae_email", as: "ae_email", type: "string" },
        ],
      ),

      // 3. Fetch meeting notes API (Gong/transcription)
      n(
        "3",
        "hitApi",
        "Gong: Fetch detailed meeting transcript and analysis",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.gong.io/v2/calls/transcript",
            headers: [
              { key: "Authorization", value: "Basic {gong_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filter",
                value:
                  '{"fromDateTime":"{meeting_date}","toDateTime":"{meeting_date}"}',
              },
            ],
            variables: [
              {
                name: "callTranscripts[0].transcript",
                customName: "full_transcript",
                type: "string",
              },
              {
                name: "callTranscripts[0].duration",
                customName: "call_duration_sec",
                type: "number",
              },
              {
                name: "callTranscripts[0].topics",
                customName: "discussed_topics",
                type: "string",
              },
              {
                name: "callTranscripts[0].questions",
                customName: "prospect_questions",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. Pull account history HubSpot
      hubspotGetNode(
        "4",
        "HubSpot: Fetch account history and deal context",
        760,
        "crm/v3/objects/companies/search?query={prospect_company}&properties=name,domain,industry,annualrevenue,numberofemployees,hs_lead_status,notes_last_updated",
        [
          {
            name: "results[0].properties.name",
            customName: "crm_company_name",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "account_industry",
            type: "string",
          },
          {
            name: "results[0].properties.annualrevenue",
            customName: "account_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.numberofemployees",
            customName: "account_employees",
            type: "string",
          },
          { name: "results[0].id", customName: "company_id", type: "string" },
        ],
      ),

      // 5. AI strategic account plan
      aiNode(
        "5",
        "AI generate comprehensive strategic account plan",
        1000,
        "You are a strategic account planning expert. Based on meeting notes, account history, and prospect context, create a comprehensive account plan. Include: executive summary, opportunity assessment, competitive landscape, stakeholder analysis, value proposition alignment, risk factors, timeline, and recommended next steps. Return JSON: {executive_summary, opportunity_size, competitive_threats, stakeholder_analysis, value_alignment, risk_factors, recommended_timeline, next_steps, win_probability, deal_size_estimate}",
        "Company: {crm_company_name} | Industry: {account_industry} | Revenue: {account_revenue} | Employees: {account_employees} | Meeting with: {prospect_name}, {prospect_title} | Type: {meeting_type} | Notes: {meeting_notes} | Topics: {discussed_topics} | Prospect questions: {prospect_questions} | Next steps discussed: {next_steps_raw} | Attendees: {attendees}",
        [
          { field: "executive_summary", as: "exec_summary", type: "string" },
          { field: "opportunity_size", as: "opportunity_size", type: "string" },
          {
            field: "competitive_threats",
            as: "competitive_threats",
            type: "string",
          },
          {
            field: "stakeholder_analysis",
            as: "stakeholder_analysis",
            type: "string",
          },
          { field: "value_alignment", as: "value_alignment", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "recommended_timeline",
            as: "recommended_timeline",
            type: "string",
          },
          { field: "next_steps", as: "plan_next_steps", type: "string" },
          { field: "win_probability", as: "win_probability", type: "string" },
          {
            field: "deal_size_estimate",
            as: "deal_size_estimate",
            type: "string",
          },
        ],
      ),

      // 6. Build stakeholder influence map runCode
      n(
        "6",
        "runCode",
        "Build stakeholder influence map and deal size classification",
        1240,
        {
          runCodeDetails: {
            code: `const stakeholders = variables.stakeholder_analysis || '';
const attendeeList = (variables.attendees || '').split(',').map(a => a.trim()).filter(Boolean);
output.stakeholder_count = attendeeList.length;
output.influence_map = attendeeList.map((a, i) => (i + 1) + '. ' + a).join('\\n');
const dealSize = parseInt((variables.deal_size_estimate || '0').replace(/[^0-9]/g, '')) || 0;
output.deal_size_category = dealSize >= 100000 ? 'enterprise' : dealSize >= 25000 ? 'mid_market' : 'smb';
output.plan_priority = dealSize >= 100000 ? 'executive_review' : dealSize >= 25000 ? 'manager_review' : 'standard';
output.review_needed = dealSize >= 50000 ? 'yes' : 'no';
output.formatted_deal_size = '$' + dealSize.toLocaleString();`,
            outputVariables: [
              { name: "stakeholder_count", type: "number" },
              { name: "influence_map", type: "string" },
              { name: "deal_size_category", type: "string" },
              { name: "plan_priority", type: "string" },
              { name: "review_needed", type: "string" },
              { name: "formatted_deal_size", type: "string" },
            ],
          },
        },
      ),

      // 7. Branch by deal size condition
      n(
        "7",
        "condition",
        "Route by deal size for approval and notification level",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "deal_size_category",
                operator: "equals",
                value: "enterprise",
              },
            ],
            logicalOperator: "OR",
          },
        },
      ),

      // 8. Save account plan CRM HubSpot
      hubspotPostNode(
        "8",
        "HubSpot: Save strategic account plan to CRM",
        1480,
        "crm/v3/objects/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"STRATEGIC ACCOUNT PLAN — {crm_company_name}\\n\\nExecutive Summary: {exec_summary}\\n\\nOpportunity: {opportunity_size} ({formatted_deal_size})\\nWin probability: {win_probability}\\nTimeline: {recommended_timeline}\\n\\nStakeholders ({stakeholder_count}):\\n{influence_map}\\n\\nCompetitive threats: {competitive_threats}\\nRisk factors: {risk_factors}\\nValue alignment: {value_alignment}\\n\\nNext steps: {plan_next_steps}","hs_timestamp":"{meeting_date}"}',
          },
        ],
        [{ name: "id", customName: "note_id", type: "string" }],
      ),

      // 9. Notify AE and leadership Slack
      slackNode(
        "9",
        "Slack: Share account plan with AE and leadership",
        1720,
        "Strategic Account Plan Generated — {crm_company_name}:\n\n{exec_summary}\n\n- Opportunity: {opportunity_size} ({formatted_deal_size})\n- Win probability: {win_probability}\n- Deal category: {deal_size_category}\n- Stakeholders: {stakeholder_count}\n- Timeline: {recommended_timeline}\n- Next steps: {plan_next_steps}\n- Priority: {plan_priority}\n- AE: {ae_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1863. POSTMARK TRANSACTIONAL SEND (7 nodes)
  // ===================================================================

  "Postmark Transactional Send": {
    summary:
      "Fires on webhook when a transactional email send is requested. Fetches recipient context from HubSpot, uses AI to generate email content, validates content compliance with runCode, branches by validation result, sends via Postmark API, and logs delivery in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Transactional email send request", "webhook_app", {
        source: "application",
        event: "email_send_requested",
        howItStarts:
          "Fires when the application requests a transactional email send (e.g., welcome email, password reset, notification), triggering content generation, compliance check, Postmark delivery, and CRM logging",
        setup:
          "Configure application webhook for email send requests. Ensure HubSpot token has contact read access. Configure OpenAI key for content generation. Set up Postmark server API token for email delivery.",
        inputFields: [],
      }),

      // 2. AI extract send request details
      aiNode(
        "2",
        "AI extract transactional email request details from webhook",
        280,
        "You are a data extraction specialist. Extract the email send request details from this webhook payload regardless of format. Find the recipient email, recipient name, email type/template, dynamic variables, sender name, and any personalization context. Return JSON: {recipient_email, recipient_name, email_type, template_id, dynamic_vars, sender_name, sender_email, urgency, context}",
        "Extract send request from: {webhook_payload}",
        [
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "recipient_name", as: "recipient_name", type: "string" },
          { field: "email_type", as: "email_type", type: "string" },
          { field: "template_id", as: "template_id", type: "string" },
          { field: "dynamic_vars", as: "dynamic_vars", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "urgency", as: "send_urgency", type: "string" },
          { field: "context", as: "email_context", type: "string" },
        ],
      ),

      // 3. Fetch recipient context HubSpot
      hubspotGetNode(
        "3",
        "HubSpot: Fetch recipient contact context",
        520,
        "crm/v3/objects/contacts/{recipient_email}?idProperty=email&properties=firstname,lastname,company,lifecyclestage,hs_email_optout",
        [
          {
            name: "properties.firstname",
            customName: "contact_first",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "contact_last",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "contact_company",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle",
            type: "string",
          },
          {
            name: "properties.hs_email_optout",
            customName: "email_opt_out",
            type: "string",
          },
        ],
      ),

      // 4. AI generate email content
      aiNode(
        "4",
        "AI generate transactional email content",
        760,
        "You are a transactional email content specialist. Generate professional, clear email content for the specified email type. Use the recipient's name and context for personalization. Keep the tone appropriate for transactional emails — helpful, clear, and action-oriented. Return JSON: {subject_line, html_body, text_body, preheader}",
        "Recipient: {contact_first} {contact_last} ({recipient_email}) at {contact_company} | Email type: {email_type} | Template: {template_id} | Dynamic vars: {dynamic_vars} | Context: {email_context} | Lifecycle: {lifecycle}",
        [
          { field: "subject_line", as: "email_subject", type: "string" },
          { field: "html_body", as: "email_html", type: "string" },
          { field: "text_body", as: "email_text", type: "string" },
          { field: "preheader", as: "email_preheader", type: "string" },
        ],
      ),

      // 5. Validate content compliance runCode
      n(
        "5",
        "runCode",
        "Validate email content compliance and opt-out status",
        1000,
        {
          runCodeDetails: {
            code: `const optOut = variables.email_opt_out === 'true' || variables.email_opt_out === true;
const subject = variables.email_subject || '';
const body = variables.email_html || variables.email_text || '';
const emailType = (variables.email_type || '').toLowerCase();
const isTransactional = ['password_reset','order_confirmation','shipping','receipt','account_verification','security_alert'].some(t => emailType.includes(t));
const hasUnsubLink = body.includes('unsubscribe') || body.includes('opt-out');
const spamWords = ['free','winner','click here','act now','limited time'];
const hasSpamWords = spamWords.some(w => (subject + ' ' + body).toLowerCase().includes(w));
output.is_compliant = !optOut || isTransactional ? 'yes' : 'no';
output.is_transactional = isTransactional ? 'yes' : 'no';
output.opt_out_blocked = optOut && !isTransactional ? 'yes' : 'no';
output.has_unsub_link = hasUnsubLink ? 'yes' : 'no';
output.spam_risk = hasSpamWords ? 'elevated' : 'low';
output.compliance_notes = optOut && !isTransactional ? 'BLOCKED: recipient opted out of non-transactional email' : 'Passed compliance checks';`,
            outputVariables: [
              { name: "is_compliant", type: "string" },
              { name: "is_transactional", type: "string" },
              { name: "opt_out_blocked", type: "string" },
              { name: "has_unsub_link", type: "string" },
              { name: "spam_risk", type: "string" },
              { name: "compliance_notes", type: "string" },
            ],
          },
        },
      ),

      // 6. Branch by validation condition
      n("6", "condition", "Check if email passes compliance validation", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "is_compliant", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Send via Postmark API (compliant path)
      n("7", "hitApi", "Postmark: Send transactional email via API", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.postmarkapp.com/email",
          headers: [
            {
              key: "X-Postmark-Server-Token",
              value: "{postmark_server_token}",
            },
            { key: "Content-Type", value: "application/json" },
            { key: "Accept", value: "application/json" },
          ],
          body: [
            { key: "From", value: "{sender_name} <{sender_email}>" },
            { key: "To", value: "{recipient_email}" },
            { key: "Subject", value: "{email_subject}" },
            { key: "HtmlBody", value: "{email_html}" },
            { key: "TextBody", value: "{email_text}" },
            { key: "MessageStream", value: "outbound" },
          ],
          variables: [
            {
              name: "MessageID",
              customName: "postmark_message_id",
              type: "string",
            },
            { name: "SubmittedAt", customName: "submitted_at", type: "string" },
            { name: "ErrorCode", customName: "error_code", type: "number" },
            { name: "Message", customName: "postmark_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
    ],
  },

  // ===================================================================
  // 1864. PRESS NEWS PERSONALIZATION (8 nodes)
  // ===================================================================

  "Press News Personalization": {
    summary:
      "Fires on webhook when a lead is assigned. Fetches recent company news via API, scrapes the company press page with runCode, uses AI to analyze news for outreach angles, generates AI news-referenced openers, branches by news recency, sends the outreach email, and logs the outreach in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Lead assigned for news-based outreach", "webhook_app", {
        source: "lead_routing",
        event: "lead_assigned",
        howItStarts:
          "Fires when a lead is assigned to an SDR, triggering research of recent company news and press releases to generate a hyper-personalized outreach email referencing current events at the prospect's company",
        setup:
          "Configure lead routing webhook for assignment events. Set up NewsAPI key for recent news lookup. Configure OpenAI key for AI analysis and content generation. Ensure HubSpot token has contact and activity logging access.",
        inputFields: [],
      }),

      // 2. AI extract lead data from webhook
      aiNode(
        "2",
        "AI extract lead details for news research",
        280,
        "You are a data extraction specialist. Extract the lead details from this webhook payload regardless of format. Find the email, name, company, domain, title, and assigned rep. Return JSON: {lead_email, lead_name, lead_company, company_domain, lead_title, assigned_rep, lead_source}",
        "Extract lead details from: {webhook_payload}",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
          { field: "lead_title", as: "lead_title", type: "string" },
          { field: "assigned_rep", as: "assigned_rep", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
        ],
      ),

      // 3. Fetch recent company news API
      n(
        "3",
        "hitApi",
        "NewsAPI: Fetch recent news articles about prospect company",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://newsapi.org/v2/everything?q={lead_company}&sortBy=publishedAt&pageSize=5&language=en&apiKey={newsapi_key}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "articles[0].title",
                customName: "news_title_1",
                type: "string",
              },
              {
                name: "articles[0].description",
                customName: "news_desc_1",
                type: "string",
              },
              {
                name: "articles[0].publishedAt",
                customName: "news_date_1",
                type: "string",
              },
              {
                name: "articles[0].url",
                customName: "news_url_1",
                type: "string",
              },
              {
                name: "articles[1].title",
                customName: "news_title_2",
                type: "string",
              },
              {
                name: "articles[1].description",
                customName: "news_desc_2",
                type: "string",
              },
              {
                name: "totalResults",
                customName: "total_news_results",
                type: "number",
              },
            ],
          },
        },
      ),

      // 4. Fetch company press page runCode
      n(
        "4",
        "runCode",
        "Analyze news recency and relevance for outreach",
        760,
        {
          runCodeDetails: {
            code: `const newsDate = variables.news_date_1 || '';
const totalResults = parseInt(variables.total_news_results) || 0;
const now = new Date();
const publishDate = newsDate ? new Date(newsDate) : new Date(0);
const daysSince = Math.floor((now.getTime() - publishDate.getTime()) / (1000 * 60 * 60 * 24));
output.days_since_news = daysSince;
output.news_recency = daysSince <= 7 ? 'very_recent' : daysSince <= 30 ? 'recent' : daysSince <= 90 ? 'moderate' : 'stale';
output.has_recent_news = daysSince <= 30 ? 'yes' : 'no';
output.news_volume = totalResults >= 10 ? 'high' : totalResults >= 3 ? 'moderate' : 'low';
output.best_news_headline = variables.news_title_1 || 'No recent news found';
output.best_news_summary = variables.news_desc_1 || '';
output.secondary_headline = variables.news_title_2 || '';
output.personalization_strength = daysSince <= 7 ? 'strong' : daysSince <= 30 ? 'good' : 'weak';`,
            outputVariables: [
              { name: "days_since_news", type: "number" },
              { name: "news_recency", type: "string" },
              { name: "has_recent_news", type: "string" },
              { name: "news_volume", type: "string" },
              { name: "best_news_headline", type: "string" },
              { name: "best_news_summary", type: "string" },
              { name: "secondary_headline", type: "string" },
              { name: "personalization_strength", type: "string" },
            ],
          },
        },
      ),

      // 5. AI analyze news for angles
      aiNode(
        "5",
        "AI analyze company news for outreach angles",
        1000,
        "You are a sales intelligence analyst. Given recent news about a prospect's company, identify the best outreach angles. Determine if the news represents growth, funding, product launch, leadership change, expansion, or other events. Map each to a relevant sales angle. Return JSON: {primary_angle, angle_type, relevance_to_solution, conversation_starter, timing_insight, secondary_angle}",
        "Company: {lead_company} | Lead: {lead_name}, {lead_title} | News 1: {best_news_headline} — {best_news_summary} | News 2: {secondary_headline} | Recency: {news_recency} ({days_since_news} days) | Volume: {news_volume}",
        [
          { field: "primary_angle", as: "primary_angle", type: "string" },
          { field: "angle_type", as: "angle_type", type: "string" },
          {
            field: "relevance_to_solution",
            as: "solution_relevance",
            type: "string",
          },
          {
            field: "conversation_starter",
            as: "conversation_starter",
            type: "string",
          },
          { field: "timing_insight", as: "timing_insight", type: "string" },
          { field: "secondary_angle", as: "secondary_angle", type: "string" },
        ],
      ),

      // 6. AI news-referenced opener
      aiNode(
        "6",
        "AI generate news-referenced outreach email",
        1240,
        "You are a personalized outreach specialist. Write a cold email that naturally references recent news about the prospect's company in the opening line. The news reference should feel organic, not forced. Connect it smoothly to your value proposition. Return JSON: {subject_line, email_body, news_opener, value_bridge, cta_text}",
        "Lead: {lead_name}, {lead_title} at {lead_company} | News: {best_news_headline} | Angle: {primary_angle} ({angle_type}) | Relevance: {solution_relevance} | Starter: {conversation_starter} | Timing: {timing_insight} | Personalization: {personalization_strength}",
        [
          { field: "subject_line", as: "news_subject", type: "string" },
          { field: "email_body", as: "news_body", type: "string" },
          { field: "news_opener", as: "news_opener", type: "string" },
          { field: "value_bridge", as: "value_bridge", type: "string" },
          { field: "cta_text", as: "news_cta", type: "string" },
        ],
      ),

      // 7. Branch by news recency condition
      n("7", "condition", "Route by news recency for send priority", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "has_recent_news", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Send outreach email + log CRM
      emailNode(
        "8",
        "Send news-referenced outreach email",
        1720,
        "{lead_email}",
        "{news_subject}",
        "{news_body}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1865. PRESS RELEASE PITCH (9 nodes)
  // ===================================================================

  "Press Release Pitch": {
    summary:
      "Fires on manual trigger when a press release is ready for journalist outreach. Parses the press release with runCode, builds a target journalist list with runCode, validates journalist emails via API, generates AI-personalized pitches per journalist, branches by publication tier, sends pitch emails, tracks engagement in Google Sheets, and logs pitch activity.",
    nodes: [
      trigger("Manual: Launch press release pitch campaign", "manual", {
        howItStarts:
          "Manually triggered when a press release is ready for distribution, initiating journalist list building, email validation, personalized pitch generation, and multi-tier outreach",
        setup:
          "Configure Hunter.io or similar API for journalist email discovery. Provide ZeroBounce API key for email validation. Configure OpenAI key for AI pitch personalization. Set up Google Sheets API for pitch tracking. Ensure email sending is configured.",
        inputFields: [
          {
            name: "press_release_text",
            type: "string",
            example: "FOR IMMEDIATE RELEASE: Company X launches...",
          },
          {
            name: "press_release_title",
            type: "string",
            example: "Company X Launches Revolutionary Product",
          },
          {
            name: "target_industry",
            type: "string",
            example: "SaaS, B2B Technology",
          },
          {
            name: "spokesperson_name",
            type: "string",
            example: "Jane Smith, CEO",
          },
          {
            name: "spokesperson_email",
            type: "string",
            example: "jane@company.com",
          },
        ],
      }),

      // 2. Parse press release content runCode
      n(
        "2",
        "runCode",
        "Parse press release for key newsworthy elements",
        280,
        {
          runCodeDetails: {
            code: `const text = variables.press_release_text || '';
const title = variables.press_release_title || '';
const lines = text.split('\\n').filter(l => l.trim());
const firstQuote = text.match(/"([^"]+)"/);
output.lead_paragraph = lines.slice(0, 3).join(' ').substring(0, 500);
output.key_quote = firstQuote ? firstQuote[1] : '';
output.word_count = text.split(/\\s+/).length;
const hasData = text.match(/(\\d+)\\s*%/) || text.match(/\\$[\\d,.]+/);
output.has_data_points = hasData ? 'yes' : 'no';
output.data_highlight = hasData ? hasData[0] : '';
const newsType = title.toLowerCase().includes('launch') ? 'product_launch' : title.toLowerCase().includes('raise') || title.toLowerCase().includes('funding') ? 'funding' : title.toLowerCase().includes('partner') ? 'partnership' : title.toLowerCase().includes('hire') || title.toLowerCase().includes('appoint') ? 'leadership' : 'general';
output.news_type = newsType;
output.embargo_detected = text.toLowerCase().includes('embargo') ? 'yes' : 'no';
output.press_hook = newsType === 'product_launch' ? 'New product addresses market gap' : newsType === 'funding' ? 'Growth trajectory and market validation' : newsType === 'partnership' ? 'Strategic alliance expands market reach' : 'Industry-relevant development';`,
            outputVariables: [
              { name: "lead_paragraph", type: "string" },
              { name: "key_quote", type: "string" },
              { name: "word_count", type: "number" },
              { name: "has_data_points", type: "string" },
              { name: "data_highlight", type: "string" },
              { name: "news_type", type: "string" },
              { name: "embargo_detected", type: "string" },
              { name: "press_hook", type: "string" },
            ],
          },
        },
      ),

      // 3. Fetch target journalist list runCode
      n(
        "3",
        "runCode",
        "Build target journalist list by industry and beat",
        520,
        {
          runCodeDetails: {
            code: `const industry = (variables.target_industry || '').toLowerCase();
const newsType = variables.news_type || 'general';
const journalistBeats = {
  'saas': ['TechCrunch','VentureBeat','SaaStr','Product Hunt'],
  'b2b': ['Forbes Tech','Business Insider','Inc.','Fast Company'],
  'fintech': ['Finextra','American Banker','PaymentsSource','The Block'],
  'healthtech': ['MobiHealthNews','Fierce Healthcare','Healthcare IT News','STAT'],
  'default': ['TechCrunch','Forbes','Business Insider','The Verge'],
};
const publications = journalistBeats[Object.keys(journalistBeats).find(k => industry.includes(k)) || 'default'] || journalistBeats.default;
output.target_publications = publications.join(', ');
output.publication_count = publications.length;
output.tier_1_pubs = publications.slice(0, 2).join(', ');
output.tier_2_pubs = publications.slice(2).join(', ');
output.pitch_angle = newsType === 'product_launch' ? 'Product innovation story' : newsType === 'funding' ? 'Growth and market opportunity' : 'Industry trend piece';
output.journalist_search_domain = publications[0].toLowerCase().replace(/\\s+/g, '') + '.com';`,
            outputVariables: [
              { name: "target_publications", type: "string" },
              { name: "publication_count", type: "number" },
              { name: "tier_1_pubs", type: "string" },
              { name: "tier_2_pubs", type: "string" },
              { name: "pitch_angle", type: "string" },
              { name: "journalist_search_domain", type: "string" },
            ],
          },
        },
      ),

      // 4. Validate journalist emails API
      n(
        "4",
        "hitApi",
        "Hunter.io: Discover journalist contacts at target publications",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hunter.io/v2/domain-search?domain={journalist_search_domain}&api_key={hunter_api_key}&department=editorial&limit=5",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "data.emails[0].value",
                customName: "journalist_email",
                type: "string",
              },
              {
                name: "data.emails[0].first_name",
                customName: "journalist_first",
                type: "string",
              },
              {
                name: "data.emails[0].last_name",
                customName: "journalist_last",
                type: "string",
              },
              {
                name: "data.emails[0].position",
                customName: "journalist_title",
                type: "string",
              },
              {
                name: "data.domain",
                customName: "publication_domain",
                type: "string",
              },
            ],
          },
        },
      ),

      // 5. AI personalized pitch per journalist
      aiNode(
        "5",
        "AI generate personalized press pitch for journalist",
        1000,
        "You are a PR pitch specialist. Write a concise, personalized pitch email to a journalist about a press release. Reference their publication and beat, lead with the most newsworthy angle, include a key quote and data point, and make it easy to write about. Return JSON: {subject_line, pitch_body, exclusive_offer, key_data_point, availability_line}",
        "Journalist: {journalist_first} {journalist_last}, {journalist_title} at {publication_domain} | Press release: {press_release_title} | Type: {news_type} | Lead: {lead_paragraph} | Quote: {key_quote} | Data: {data_highlight} | Hook: {press_hook} | Angle: {pitch_angle} | Spokesperson: {spokesperson_name}",
        [
          { field: "subject_line", as: "pitch_subject", type: "string" },
          { field: "pitch_body", as: "pitch_body", type: "string" },
          { field: "exclusive_offer", as: "exclusive_offer", type: "string" },
          { field: "key_data_point", as: "pitch_data_point", type: "string" },
          {
            field: "availability_line",
            as: "availability_line",
            type: "string",
          },
        ],
      ),

      // 6. Branch by publication tier condition
      n(
        "6",
        "condition",
        "Route by publication tier for outreach priority",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "publication_count",
                operator: "greater_than",
                value: "1",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Send pitches email (has journalists path)
      emailNode(
        "7",
        "Send personalized pitch to journalist",
        1480,
        "{journalist_email}",
        "{pitch_subject}",
        "{pitch_body}",
      ),

      // 8. Track pitch engagement Sheets
      sheetsNode(
        "8",
        "Google Sheets: Log pitch in tracking spreadsheet",
        1720,
        "PressOutreach",
        '[["{press_release_title}","{journalist_first} {journalist_last}","{journalist_email}","{publication_domain}","{news_type}","{pitch_subject}","sent","",""]]',
      ),

      // 9. Log pitch activity Sheets
      sheetsNode(
        "9",
        "Google Sheets: Update campaign-level pitch metrics",
        1960,
        "CampaignMetrics",
        '[["{press_release_title}","{publication_count}","{tier_1_pubs}","{tier_2_pubs}","{news_type}","{pitch_angle}","{embargo_detected}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "9", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1866. PROCUREMENT STAKEHOLDER FINDER (8 nodes)
  // ===================================================================

  "Procurement Stakeholder Finder": {
    summary:
      "Fires on webhook when a deal enters procurement stage. Fetches account org structure via API, enriches procurement contacts through Clearbit, discovers contact emails via API, uses AI to analyze stakeholder roles, generates AI procurement-tailored emails, branches by stakeholder priority, and creates multi-thread outreach tasks in HubSpot.",
    nodes: [
      trigger("Webhook: Deal enters procurement stage", "webhook_app", {
        source: "crm_pipeline",
        event: "deal_stage_changed",
        howItStarts:
          "Fires when an active deal moves to the procurement or legal review stage, triggering stakeholder discovery and multi-threaded procurement-specific outreach to navigate the buying committee",
        setup:
          "Configure CRM webhook for deal stage change events. Set up Clearbit API for contact enrichment. Provide Hunter.io API key for email discovery. Configure OpenAI key for AI analysis and email generation. Ensure HubSpot token has contact, deal, and task creation access.",
        inputFields: [],
      }),

      // 2. AI extract deal and procurement stage data
      aiNode(
        "2",
        "AI extract deal procurement stage details from webhook",
        280,
        "You are a data extraction specialist. Extract the deal and procurement stage details from this webhook payload regardless of format. Find the company name, domain, deal name, deal value, deal stage, champion name, champion email, and any procurement-specific context. Return JSON: {company_name, company_domain, deal_name, deal_value, deal_stage, champion_name, champion_email, procurement_context, deal_id}",
        "Extract procurement stage details from: {webhook_payload}",
        [
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "champion_name", as: "champion_name", type: "string" },
          { field: "champion_email", as: "champion_email", type: "string" },
          {
            field: "procurement_context",
            as: "procurement_context",
            type: "string",
          },
          { field: "deal_id", as: "deal_id", type: "string" },
        ],
      ),

      // 3. Fetch account org structure API
      n(
        "3",
        "hitApi",
        "Hunter.io: Discover procurement and legal contacts",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hunter.io/v2/domain-search?domain={company_domain}&api_key={hunter_api_key}&department=finance,legal,executive&limit=10",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "data.emails",
                customName: "discovered_contacts",
                type: "string",
              },
              {
                name: "data.emails[0].value",
                customName: "proc_email_1",
                type: "string",
              },
              {
                name: "data.emails[0].first_name",
                customName: "proc_first_1",
                type: "string",
              },
              {
                name: "data.emails[0].last_name",
                customName: "proc_last_1",
                type: "string",
              },
              {
                name: "data.emails[0].position",
                customName: "proc_title_1",
                type: "string",
              },
              {
                name: "data.emails[1].value",
                customName: "proc_email_2",
                type: "string",
              },
              {
                name: "data.emails[1].first_name",
                customName: "proc_first_2",
                type: "string",
              },
              {
                name: "data.emails[1].position",
                customName: "proc_title_2",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. Enrich procurement contacts Clearbit
      n("4", "hitApi", "Clearbit: Enrich procurement contact profiles", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person-stream.clearbit.com/v2/combined/find?email={proc_email_1}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "person.employment.title",
              customName: "enriched_proc_title",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "proc_seniority",
              type: "string",
            },
            {
              name: "person.employment.role",
              customName: "proc_role",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_org",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "org_size",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI analyze stakeholder roles
      aiNode(
        "5",
        "AI analyze procurement stakeholder roles and influence",
        1000,
        "You are a deal strategy analyst specializing in procurement navigation. Given discovered contacts and their roles, analyze each stakeholder's likely influence in the procurement process. Identify who is the decision maker, influencer, gatekeeper, and potential blocker. Return JSON: {stakeholder_map, decision_maker, key_influencer, potential_blocker, engagement_sequence, stakeholder_priorities, risk_assessment}",
        "Company: {company_name} | Deal: {deal_name} ({deal_value}) | Champion: {champion_name} | Contacts: 1) {proc_first_1} {proc_last_1}, {enriched_proc_title} ({proc_seniority}) 2) {proc_first_2}, {proc_title_2} | Org size: {org_size} | Context: {procurement_context}",
        [
          { field: "stakeholder_map", as: "stakeholder_map", type: "string" },
          { field: "decision_maker", as: "decision_maker", type: "string" },
          { field: "key_influencer", as: "key_influencer", type: "string" },
          {
            field: "potential_blocker",
            as: "potential_blocker",
            type: "string",
          },
          {
            field: "engagement_sequence",
            as: "engagement_sequence",
            type: "string",
          },
          {
            field: "stakeholder_priorities",
            as: "stakeholder_priorities",
            type: "string",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
        ],
      ),

      // 6. AI procurement-tailored emails
      aiNode(
        "6",
        "AI generate procurement-tailored stakeholder emails",
        1240,
        "You are a procurement communication specialist. Write a professional email tailored for procurement stakeholders that addresses compliance, security, vendor requirements, and procurement-specific concerns. Be factual, concise, and reference relevant certifications. Return JSON: {proc_subject, proc_body, compliance_highlight, security_summary, vendor_credentials, cta_text}",
        "Stakeholder: {proc_first_1} {proc_last_1}, {enriched_proc_title} at {company_name} | Deal: {deal_name} ({deal_value}) | Role: {proc_role} | Seniority: {proc_seniority} | Decision maker: {decision_maker} | Priorities: {stakeholder_priorities} | Risk: {risk_assessment} | Champion: {champion_name}",
        [
          { field: "proc_subject", as: "proc_subject", type: "string" },
          { field: "proc_body", as: "proc_body", type: "string" },
          {
            field: "compliance_highlight",
            as: "compliance_highlight",
            type: "string",
          },
          { field: "security_summary", as: "security_summary", type: "string" },
          {
            field: "vendor_credentials",
            as: "vendor_credentials",
            type: "string",
          },
          { field: "cta_text", as: "proc_cta", type: "string" },
        ],
      ),

      // 7. Branch by stakeholder priority condition
      n(
        "7",
        "condition",
        "Route by stakeholder priority for outreach urgency",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "proc_seniority",
                operator: "equals",
                value: "director",
              },
            ],
            logicalOperator: "OR",
          },
        },
      ),

      // 8. Create multi-thread tasks HubSpot
      hubspotPostNode(
        "8",
        "HubSpot: Create multi-thread procurement outreach tasks",
        1720,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"Procurement thread: {proc_first_1} {proc_last_1} at {company_name}","hs_task_body":"Deal: {deal_name} ({deal_value})\\nStakeholder: {proc_first_1} {proc_last_1}, {enriched_proc_title}\\nRole: {proc_role} | Seniority: {proc_seniority}\\nDecision maker: {decision_maker}\\nInfluencer: {key_influencer}\\nBlocker risk: {potential_blocker}\\nEngagement sequence: {engagement_sequence}\\nEmail subject: {proc_subject}\\nCompliance: {compliance_highlight}","hs_task_priority":"HIGH","hs_task_type":"EMAIL","hubspot_owner_id":"{owner_id}"}',
          },
        ],
        [
          { name: "id", customName: "proc_task_id", type: "string" },
          {
            name: "properties.hs_task_subject",
            customName: "proc_task_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1867. PROCUREMENT SUPPORT EMAIL (7 nodes)
  // ===================================================================

  "Procurement Support Email": {
    summary:
      "Fires on webhook when a procurement support request is received. Fetches deal and contract details from HubSpot, pulls compliance certifications with runCode, uses AI to generate a procurement summary email, branches by deal size, sends the email, and logs the activity in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Procurement support request received", "webhook_app", {
        source: "sales_process",
        event: "procurement_support_requested",
        howItStarts:
          "Fires when a sales rep or prospect requests procurement support documentation, triggering automatic assembly of compliance certifications, contract terms, and a tailored procurement summary email",
        setup:
          "Configure webhook for procurement support requests. Ensure HubSpot token has deal and contact read access. Configure OpenAI key for AI email generation. Set up email sending for procurement communications.",
        inputFields: [],
      }),

      // 2. AI extract procurement request from webhook
      aiNode(
        "2",
        "AI extract procurement request details from webhook payload",
        280,
        "You are a data extraction specialist. Extract the procurement support request details from this webhook payload regardless of format. Find the requester email, requester name, company, deal reference, specific documents requested, deadline, and any procurement requirements mentioned. Return JSON: {requester_email, requester_name, requester_company, deal_reference, docs_requested, deadline, procurement_requirements, requester_role}",
        "Extract procurement request details from: {webhook_payload}",
        [
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          {
            field: "requester_company",
            as: "requester_company",
            type: "string",
          },
          { field: "deal_reference", as: "deal_reference", type: "string" },
          { field: "docs_requested", as: "docs_requested", type: "string" },
          { field: "deadline", as: "deadline", type: "string" },
          {
            field: "procurement_requirements",
            as: "procurement_requirements",
            type: "string",
          },
          { field: "requester_role", as: "requester_role", type: "string" },
        ],
      ),

      // 3. Fetch deal contract details HubSpot
      hubspotGetNode(
        "3",
        "HubSpot: Fetch deal details and contract context",
        520,
        "crm/v3/objects/deals/{deal_reference}?properties=dealname,amount,dealstage,closedate,pipeline,description,hs_acv,contract_type",
        [
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
          {
            name: "properties.amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
          {
            name: "properties.closedate",
            customName: "close_date",
            type: "string",
          },
          {
            name: "properties.description",
            customName: "deal_description",
            type: "string",
          },
          {
            name: "properties.hs_acv",
            customName: "annual_contract_value",
            type: "string",
          },
        ],
      ),

      // 4. Pull compliance certifications runCode
      n(
        "4",
        "runCode",
        "Compile compliance certifications and procurement documents",
        760,
        {
          runCodeDetails: {
            code: `const docsRequested = (variables.docs_requested || '').toLowerCase();
const requirements = (variables.procurement_requirements || '').toLowerCase();
const certs = [];
const docs = [];
certs.push('SOC 2 Type II (valid through Dec 2026)');
certs.push('ISO 27001:2022 Certified');
if (docsRequested.includes('gdpr') || requirements.includes('gdpr') || requirements.includes('privacy')) {
  certs.push('GDPR Compliant — DPA available');
}
if (docsRequested.includes('hipaa') || requirements.includes('hipaa') || requirements.includes('health')) {
  certs.push('HIPAA BAA available upon request');
}
certs.push('99.9% SLA Uptime Guarantee');
docs.push('Master Service Agreement (MSA)');
docs.push('Data Processing Agreement (DPA)');
docs.push('Security Whitepaper');
docs.push('Vendor Risk Assessment Questionnaire');
if (docsRequested.includes('insurance') || requirements.includes('insurance')) {
  docs.push('Certificate of Insurance (COI)');
}
if (docsRequested.includes('pentest') || requirements.includes('penetration')) {
  docs.push('Annual Penetration Test Summary');
}
output.certifications = certs.join('; ');
output.available_documents = docs.join('; ');
output.cert_count = certs.length;
output.doc_count = docs.length;
const dealAmt = parseFloat(variables.deal_amount || '0');
output.deal_size_tier = dealAmt >= 100000 ? 'enterprise' : dealAmt >= 25000 ? 'mid_market' : 'smb';
output.requires_legal_review = dealAmt >= 50000 ? 'yes' : 'no';`,
            outputVariables: [
              { name: "certifications", type: "string" },
              { name: "available_documents", type: "string" },
              { name: "cert_count", type: "number" },
              { name: "doc_count", type: "number" },
              { name: "deal_size_tier", type: "string" },
              { name: "requires_legal_review", type: "string" },
            ],
          },
        },
      ),

      // 5. AI procurement summary email
      aiNode(
        "5",
        "AI generate procurement support summary email",
        1000,
        "You are a procurement communications specialist. Write a professional, comprehensive email that addresses the procurement team's requests. Include relevant certifications, available documents, and key compliance information. Be organized with clear sections, proactive in addressing common procurement concerns, and include a clear next step. Return JSON: {subject_line, email_body, compliance_section, documents_section, next_steps}",
        "Requester: {requester_name}, {requester_role} at {requester_company} | Deal: {deal_name} ({deal_amount}) | Stage: {deal_stage} | Docs requested: {docs_requested} | Requirements: {procurement_requirements} | Deadline: {deadline} | Certifications: {certifications} | Available docs: {available_documents} | ACV: {annual_contract_value} | Legal review needed: {requires_legal_review}",
        [
          { field: "subject_line", as: "proc_email_subject", type: "string" },
          { field: "email_body", as: "proc_email_body", type: "string" },
          {
            field: "compliance_section",
            as: "compliance_section",
            type: "string",
          },
          {
            field: "documents_section",
            as: "documents_section",
            type: "string",
          },
          { field: "next_steps", as: "proc_next_steps", type: "string" },
        ],
      ),

      // 6. Branch by deal size condition
      n("6", "condition", "Route by deal size for approval workflow", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "deal_size_tier",
              operator: "equals",
              value: "enterprise",
            },
          ],
          logicalOperator: "OR",
        },
      }),

      // 7. Send email + log CRM
      emailNode(
        "7",
        "Send procurement support email with documentation",
        1480,
        "{requester_email}",
        "{proc_email_subject}",
        "{proc_email_body}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },
};
